import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock3,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Mail,
  PartyPopper,
  Phone,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';
import dayjs from '../../../lib/day';
import { useUser } from '../../../contexts/UserContext';
import PaymentPlanAuthorizationTerms, {
  buildPaymentPlanAuthorizationTermsSections,
  buildPaymentPlanAuthorizationPayload,
  createPaymentPlanAuthorizationState,
  getPaymentPlanAuthorizationSignerName,
  isPaymentPlanAuthorizationComplete,
} from '../../paymentPlans/PaymentPlanAuthorizationTerms';
import {
  getDefaultFirstInstallmentDate,
  getLatestFirstInstallmentDate,
} from '../../paymentPlans/paymentPlanDateUtils';
import {
  useApprovePaymentPlan,
  useAuthorizePaymentPlanSetup,
  useCancelPaymentPlan,
  useCreateInvoice,
  useCreatePaymentPlanBillingMethodSession,
  useCreatePaymentPlanPayoffSession,
  useDeleteInvoice,
  useConfirmInvoice,
  useFetchBillingCongratulationsEmail,
  useFetchPaymentPlanSummary,
  useRejectPaymentPlan,
  useSendBillingCongratulationsEmail,
  useSetPaymentPlanVisibility,
  useStartPaymentPlan,
  useUpdatePaymentPlanSetup,
  useUpdateInvoice,
} from '../../../services/billing';

const PaymentPlanModalGlobalStyle = createGlobalStyle`
  .billing-plan-modal .modal-content {
    border: 0;
    border-radius: 18px;
    box-shadow: 0 30px 80px rgba(15, 28, 51, 0.18);
    overflow: hidden;
  }
  .billing-plan-modal .modal-dialog {
    max-width: 720px;
  }
  .billing-plan-modal .modal-backdrop.show {
    opacity: 0.55;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 1.25rem 0 0.85rem;
  color: #0f1c33;

  h4 {
    margin: 0;
    font-weight: 700;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }

  .section-sub {
    margin-left: auto;
    font-size: 0.78rem;
    color: #7a8699;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 28, 51, 0.04);
  overflow: hidden;
  margin-bottom: 1.25rem;
`;

const CardBody = styled.div`
  padding: 1.25rem 1.4rem;
`;

const DisplayStyled = styled.div`
  margin-bottom: 0.5rem;

  & > label {
    display: block;
    font-weight: 500;
    color: #6b7689;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.15rem;
  }

  & > span {
    font-weight: 500;
    color: #0f1c33;
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const CongratsStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${({ $sent }) =>
    $sent
      ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
      : 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)'};
  border: 1px solid ${({ $sent }) => ($sent ? '#bbf7d0' : '#fcd34d')};
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $sent }) =>
      $sent
        ? 'linear-gradient(120deg, transparent, rgba(187, 247, 208, 0.4), transparent)'
        : 'linear-gradient(120deg, transparent, rgba(252, 211, 77, 0.35), transparent)'};
    background-size: 200% 100%;
    animation: ${shimmer} 5s ease-in-out infinite;
    pointer-events: none;
  }

  .strip-icon {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $sent }) => ($sent ? '#16a34a' : '#f59e0b')};
    color: #fff;
    box-shadow: 0 4px 12px
      ${({ $sent }) =>
        $sent ? 'rgba(22, 163, 74, 0.25)' : 'rgba(245, 158, 11, 0.3)'};
  }

  .strip-content {
    flex: 1 1 auto;
    min-width: 0;
    z-index: 1;
  }

  .strip-title {
    font-weight: 700;
    color: #0f1c33;
    font-size: 0.95rem;
    line-height: 1.25;
  }

  .strip-meta {
    font-size: 0.83rem;
    color: #4b5563;
    margin-top: 0.1rem;
  }

  .strip-action {
    flex: 0 0 auto;
    z-index: 1;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
    .strip-action {
      width: 100%;
    }
    .strip-action .btn {
      width: 100%;
    }
  }
`;

const PlanSummaryCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1.25rem;
  padding: 1.1rem 1.4rem;
  background:
    radial-gradient(
      800px 200px at -10% -10%,
      rgba(99, 102, 241, 0.08),
      transparent 60%
    ),
    #ffffff;

  .left {
    min-width: 0;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.18rem 0.55rem;
    border-radius: 999px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    margin-bottom: 0.55rem;
  }

  .title {
    margin: 0;
    color: #0f1c33;
    font-weight: 700;
    font-size: 1.1rem;
    letter-spacing: -0.01em;
  }

  .meta {
    color: #6b7689;
    font-size: 0.9rem;
    margin: 0.25rem 0 0;
    max-width: 520px;
    line-height: 1.45;
  }

  .visibility-row {
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    margin-top: 0.9rem;
    padding-top: 0.85rem;
    border-top: 1px solid #eef2f7;
  }

  .visibility-copy {
    min-width: 0;
  }

  .visibility-label {
    color: #0f1c33;
    font-size: 0.88rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .visibility-meta {
    color: #7a8699;
    font-size: 0.8rem;
    line-height: 1.35;
    margin-top: 0.08rem;
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.6rem;
  }

  .balance-block {
    text-align: right;
  }

  .balance-label {
    color: #7a8699;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
  }

  .balance-value {
    display: block;
    color: #0f1c33;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .balance-meta {
    color: #6b7689;
    font-size: 0.8rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    .right {
      align-items: stretch;
    }
    .balance-block {
      text-align: left;
    }
    .cta {
      width: 100%;
    }
    .cta .btn {
      width: 100%;
    }
    .visibility-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      width: 100%;
    }
  }
`;

const ModalHero = styled.div`
  position: relative;
  padding: 1.5rem 1.6rem 1.2rem;
  background:
    radial-gradient(
      1200px 200px at -10% -10%,
      rgba(99, 102, 241, 0.12),
      transparent 60%
    ),
    radial-gradient(
      800px 200px at 110% 0%,
      rgba(34, 197, 94, 0.08),
      transparent 60%
    ),
    #ffffff;
  border-bottom: 1px solid #eef2f7;

  .hero-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: start;
    padding-right: 2.25rem;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    margin-bottom: 0.6rem;
  }

  .hero-title {
    margin: 0;
    color: #0f1c33;
    font-weight: 700;
    font-size: 1.3rem;
    letter-spacing: -0.015em;
  }

  .hero-sub {
    margin: 0.3rem 0 0;
    color: #6b7689;
    max-width: 640px;
    font-size: 0.93rem;
    line-height: 1.5;
  }

  .hero-balance {
    text-align: right;
  }

  .hero-balance .label {
    color: #7a8699;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
  }

  .hero-balance .value {
    display: block;
    color: #0f1c33;
    font-size: 1.7rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .hero-balance .meta {
    color: #6b7689;
    font-size: 0.82rem;
  }

  .close-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    border: 0;
    background: transparent;
    color: #6b7689;
    padding: 6px;
    border-radius: 999px;
    line-height: 0;
    cursor: pointer;
    transition: all 0.15s ease;
    z-index: 2;
  }

  .close-btn:hover {
    background: #f1f5f9;
    color: #0f1c33;
  }

  @media (max-width: 640px) {
    .hero-row {
      grid-template-columns: 1fr;
      padding-right: 1.75rem;
    }
    .hero-balance {
      text-align: left;
    }
  }
`;

const TermChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.45rem 0 0.85rem;
`;

const Chip = styled.button`
  appearance: none;
  border: 1px solid ${({ $active }) => ($active ? '#4f46e5' : '#dde3ec')};
  background: ${({ $active }) => ($active ? '#eef2ff' : '#fff')};
  color: ${({ $active }) => ($active ? '#3730a3' : '#0f1c33')};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  font-size: 0.85rem;
  border-radius: 999px;
  padding: 0.32rem 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #4f46e5;
    color: #3730a3;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem 1.5rem;
`;

const ControlBlock = styled.div`
  .control-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
  }

  .control-label .label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7689;
    font-weight: 700;
  }

  .control-label .value {
    color: #0f1c33;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  input[type='range'] {
    width: 100%;
    accent-color: #4f46e5;
  }

  .control-input {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.45rem;
  }

  .control-input .prefix {
    color: #6b7689;
    font-weight: 500;
  }
`;

const InlineMoneyEditor = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.2rem 0;
  color: #0f1c33;
  font-weight: 800;
  font-size: 1.7rem;
  letter-spacing: -0.02em;
  line-height: 1.1;

  input {
    width: 8.5rem;
    border: 0;
    border-bottom: 2px solid #c7d2fe;
    border-radius: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    padding: 0.1rem 0;
    outline: none;
  }

  input:focus {
    border-bottom-color: #4f46e5;
  }

  @media (max-width: 640px) {
    font-size: 1.45rem;
    input {
      width: 7.25rem;
    }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin: 1.1rem 0 0.25rem;
  padding: 0.9rem 1rem;
  background: #f7f8fb;
  border: 1px solid #eef2f7;
  border-radius: 12px;

  .stat .label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #7a8699;
    font-weight: 700;
  }

  .stat .value {
    color: #0f1c33;
    font-weight: 700;
    font-size: 1rem;
    margin-top: 0.1rem;
    font-variant-numeric: tabular-nums;
  }

  .stat .accent {
    color: #4338ca;
  }
`;

const AuthorizationEvidenceButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border-radius: 999px;
    padding: 0.28rem 0.65rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: #334155;
    border-color: #cbd5e1;
    background: #ffffff;
  }

  &&:hover,
  &&:focus {
    color: #1e40af;
    border-color: #bfdbfe;
    background: #eff6ff;
  }
`;

const AuthorizationCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.85rem;
`;

const AuthorizationEvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0.7rem;
  margin-bottom: 1rem;
`;

const AuthorizationEvidenceTile = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 0.75rem;

  .label {
    color: #64748b;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 800;
    margin-bottom: 0.18rem;
  }

  .value {
    color: #0f1c33;
    font-size: 0.92rem;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }
`;

const AuthorizationEvidenceSection = styled.div`
  margin-top: 1rem;

  .section-title {
    color: #0f1c33;
    font-size: 0.88rem;
    font-weight: 800;
    margin-bottom: 0.45rem;
  }
`;

const AuthorizationModalTabs = styled.div`
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.2rem;
  border: 1px solid #dbe4f0;
  border-radius: 999px;
  background: #f8fafc;
  margin-bottom: 1rem;
`;

const AuthorizationModalTab = styled.button`
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#1e40af' : '#64748b')};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(15, 28, 51, 0.12)' : 'none'};
  font-size: 0.8rem;
  font-weight: 750;
  padding: 0.34rem 0.75rem;
`;

const AuthorizationEvidenceTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;

  .row {
    display: grid;
    grid-template-columns: minmax(150px, 0.38fr) minmax(0, 1fr);
    margin: 0;
    border-top: 1px solid #eef2f7;
  }

  .row:first-child {
    border-top: 0;
  }

  .key,
  .value {
    padding: 0.5rem 0.65rem;
    font-size: 0.82rem;
    line-height: 1.35;
  }

  .key {
    color: #64748b;
    font-weight: 750;
    background: #f8fafc;
  }

  .value {
    color: #0f1c33;
    overflow-wrap: anywhere;
  }
`;

const AuthorizationTermsViewer = styled.div`
  border: 1px solid #dbe4f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 1rem;

  .terms-version {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 750;
    margin-bottom: 0.85rem;
    overflow-wrap: anywhere;
  }

  .terms-section + .terms-section {
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px solid #eef2f7;
  }

  .terms-title {
    color: #0f1c33;
    font-size: 0.84rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.24rem;
  }

  .terms-body {
    color: #334155;
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0;
  }
`;

const AuthorizationRawJson = styled.pre`
  max-height: 260px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 0.85rem;
  font-size: 0.76rem;
  line-height: 1.45;
`;

const PhaseSection = styled.section`
  margin-top: 0.9rem;
  border: 1px solid #e6ebf3;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;

  &[data-variant='today'] {
    border-color: #d8defe;
    background: linear-gradient(180deg, #f5f7ff 0%, #ffffff 60%);
    box-shadow: 0 1px 2px rgba(79, 70, 229, 0.06);
  }

  &:first-of-type {
    margin-top: 0;
  }
`;

const PhaseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 1.1rem 0.45rem;
  border-bottom: 1px solid transparent;
`;

const PhaseEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ $variant }) => ($variant === 'today' ? '#4338ca' : '#475569')};

  .phase-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: ${({ $variant }) =>
      $variant === 'today' ? '#4f46e5' : '#cbd5f5'};
    color: ${({ $variant }) => ($variant === 'today' ? '#ffffff' : '#1e293b')};
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0;
  }
`;

const PhaseAside = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

const PhaseBody = styled.div`
  padding: 0.45rem 1.1rem 1rem;
`;

const PhaseFootnote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.7rem;
  font-size: 0.84rem;
  color: ${({ $variant }) => ($variant === 'today' ? '#3730a3' : '#475569')};
  background: ${({ $variant }) =>
    $variant === 'today' ? '#eef2ff' : '#f7f8fb'};
  border: 1px solid
    ${({ $variant }) => ($variant === 'today' ? '#c7d2fe' : '#e6ebf3')};
  padding: 0.38rem 0.65rem;
  border-radius: 8px;
  font-weight: 600;

  svg {
    flex: 0 0 auto;
  }
`;

const ControlHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: #6b7689;
  line-height: 1.35;
`;

const SchedulePreviewHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 1rem 0 0.4rem;

  .heading {
    color: #0f1c33;
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: -0.005em;
  }

  .hint {
    color: #7a8699;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }
`;

const ScheduleList = styled.div`
  margin-top: 0.35rem;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  max-height: 260px;
  overflow-y: auto;
`;

const ScheduleHeader = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 120px 120px;
  gap: 0.75rem;
  align-items: center;
  padding: 0.55rem 0.9rem;
  background: #f7f8fb;
  color: #6b7689;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  position: sticky;
  top: 0;
  z-index: 1;

  @media (max-width: 640px) {
    grid-template-columns: 32px 1fr 120px;
    .col-amount {
      display: none;
    }
  }
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 120px 120px;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.9rem;
  border-top: 1px solid #eef2f7;
  font-size: 0.92rem;
  color: #0f1c33;
  background: ${({ $isDeposit, $isPaid }) =>
    $isDeposit || $isPaid ? '#f5f7ff' : '#fff'};

  ${({ $isDeposit, $isPaid }) =>
    $isDeposit || $isPaid
      ? `
    border-bottom: 2px solid #dde3ec;
  `
      : ''}

  ${({ $isDeposit }) =>
    $isDeposit
      ? `
    border-top: 0;
  `
      : ''}

  .row-num {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#4f46e5' : '#f1f5f9'};
    color: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#ffffff' : '#475569'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.78rem;
  }

  .row-label {
    font-weight: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? 700 : 500};
    color: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#3730a3' : '#0f1c33'};
  }

  .row-status {
    font-size: 0.78rem;
    color: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#4338ca' : '#6b7689'};
    font-weight: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? 600 : 400};
  }

  .row-date,
  .row-amount {
    color: #475569;
    font-variant-numeric: tabular-nums;
  }

  .row-date {
    font-weight: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? 700 : 500};
    color: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#3730a3' : '#475569'};
  }

  .row-amount {
    text-align: right;
    font-weight: 700;
    color: ${({ $isDeposit, $isPaid }) =>
      $isDeposit || $isPaid ? '#3730a3' : '#0f1c33'};
  }

  @media (max-width: 640px) {
    grid-template-columns: 32px 1fr 110px;
    .row-amount {
      grid-column: 2 / 4;
      text-align: left;
    }
    .row-date {
      grid-column: 2 / 4;
    }
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1.1rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $variant }) =>
    ({
      requested: '#fef3c7',
      setup_pending: '#dbeafe',
      processing: '#e0f2fe',
      active: '#dcfce7',
      past_due: '#fee2e2',
      failed: '#fee2e2',
      completed: '#dcfce7',
      cancelled: '#e5e7eb',
      rejected: '#e5e7eb',
    })[$variant] || '#e5e7eb'};
  color: ${({ $variant }) =>
    ({
      requested: '#92400e',
      setup_pending: '#1e40af',
      processing: '#075985',
      active: '#166534',
      past_due: '#991b1b',
      failed: '#991b1b',
      completed: '#166534',
      cancelled: '#374151',
      rejected: '#374151',
    })[$variant] || '#374151'};
`;

const InvoiceCardStyled = styled.div`
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  background: #fff;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 28, 51, 0.04);
  overflow: hidden;

  .invoice-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1.1rem 1.3rem 0.75rem;
  }

  .invoice-top h5 {
    margin: 0 0 0.25rem;
    color: #0f1c33;
    font-weight: 700;
    font-size: 1rem;
  }

  .invoice-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem 1.5rem;
    padding: 0 1.3rem 1rem;
  }

  .invoice-footer {
    display: flex;
    /* Pay now + Set up payment plan must stack cleanly on phones. */
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.85rem 1.3rem;
    border-top: 1px solid #eef2f7;
    background: #f9fafc;
  }

  .invoice-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
  }

  @media (max-width: 640px) {
    .invoice-grid {
      grid-template-columns: 1fr;
    }
    .invoice-top {
      flex-direction: column;
    }
    .invoice-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
`;

const InvoiceStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${({ $status }) =>
    ({
      draft: '#fef3c7',
      open: '#fee2e2',
      sent: '#fee2e2',
      paid: '#dcfce7',
      // Violet plan family: a plan-covered invoice is AGREED to be paid —
      // the red "payment due" tone reads like something is wrong.
      plan: '#ede9fe',
      void: '#e5e7eb',
      uncollectible: '#fee2e2',
    })[$status] || '#e5e7eb'};
  color: ${({ $status }) =>
    ({
      draft: '#92400e',
      open: '#991b1b',
      sent: '#991b1b',
      paid: '#166534',
      plan: '#6d28d9',
      void: '#374151',
      uncollectible: '#991b1b',
    })[$status] || '#374151'};
`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount) || 0);

const getDefaultManualInvoiceDueDate = () =>
  dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD');

const formatCents = (cents) => formatCurrency((Number(cents) || 0) / 100);

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '--';
  return Number.isInteger(parsed)
    ? `${parsed}%`
    : `${parsed.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
};

const STATUS_LABELS = {
  draft: 'Draft',
  open: 'Payment due',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

const PLAN_STATUS = {
  requested: { label: 'Awaiting approval', Icon: Clock3 },
  setup_pending: { label: 'Awaiting down payment', Icon: CreditCard },
  active: { label: 'Active', Icon: CheckCircle2 },
  past_due: { label: 'Past due', Icon: Clock3 },
  failed: { label: 'Needs recovery', Icon: XCircle },
  completed: { label: 'Completed', Icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', Icon: XCircle },
  rejected: { label: 'Rejected', Icon: XCircle },
};

const PAYMENT_PLAN_CLIENT_PAYOFF_STATUSES = ['active', 'past_due', 'failed'];
const PAYMENT_PLAN_CANCELLABLE_STATUSES = [
  'setup_pending',
  'active',
  'past_due',
  'failed',
];
const mutationErrorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.message || fallback;

const getPaymentPlanDownPayment = (plan) =>
  (plan?.installments || []).find(
    (installment) => Number(installment.sequence) === 0,
  ) || null;

// ACH charges sit in `processing` for days while the bank debit clears;
// spell that out instead of the bare status word.
const installmentStatusLabel = (status) =>
  status === 'processing' ? 'processing (bank payment clearing)' : status;

const isPaymentPlanDepositProcessing = (plan) => {
  if (!plan) return false;
  const downPayment = getPaymentPlanDownPayment(plan);
  return (
    plan.metadata?.depositStatus === 'processing' ||
    downPayment?.status === 'processing'
  );
};

const openCheckoutUrl = (url) => {
  if (!url) return;
  // Same-tab navigation: window.open from an async mutation callback is
  // popup-blocked on iOS Safari, silently stranding the client before payment.
  window.location.assign(url);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const computeSchedule = ({
  balanceCents,
  termMonths,
  downPaymentCents,
  firstInstallmentDate,
  installmentDueDates,
}) => {
  if (!balanceCents || !termMonths) return null;
  const dp = clamp(Math.round(downPaymentCents || 0), 0, balanceCents);
  const remaining = balanceCents - dp;
  const base = Math.floor(remaining / termMonths);
  const rem = remaining % termMonths;
  const installmentCents = Array.from(
    { length: termMonths },
    (_, i) => base + (i < rem ? 1 : 0),
  );
  const firstDate =
    firstInstallmentDate && dayjs(firstInstallmentDate).isValid()
      ? dayjs(firstInstallmentDate)
      : dayjs(getDefaultFirstInstallmentDate());
  return {
    totalCents: balanceCents,
    downPaymentCents: dp,
    remainingCents: remaining,
    termMonths,
    installmentCents,
    installments: installmentCents.map((amountCents, index) => ({
      amountCents,
      dueDate:
        installmentDueDates?.[index] ||
        firstDate.add(index, 'month').format('YYYY-MM-DD'),
    })),
    monthlyTopCents: installmentCents[0] || 0,
  };
};

const valueForEvidence = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '--';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatAuthorizationTimestampForModal = (value) => {
  if (!value) return '--';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return valueForEvidence(value);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${parsed.format('MMM D, YYYY h:mm A')}${timezone ? ` (${timezone})` : ''}`;
};

const valueForAuthorizationModal = (row) => {
  if (
    row.section === 'Authorization' &&
    ['Accepted at server', 'Accepted at client', 'Invalidated at'].includes(
      row.field,
    )
  ) {
    return formatAuthorizationTimestampForModal(
      row.value === '--' ? null : row.value,
    );
  }
  return row.value;
};

const ManualInvoiceModal = ({ show, onHide, claimId }) => {
  const [fields, setFields] = useState({
    invoiceAmount: '',
    invoiceDueDate: getDefaultManualInvoiceDueDate(),
    invoiceNotes: '',
  });
  const createInvoice = useCreateInvoice(claimId, () => {
    toast.success('Draft invoice created.');
    onHide();
  });

  useEffect(() => {
    if (!show) return;
    setFields({
      invoiceAmount: '',
      invoiceDueDate: getDefaultManualInvoiceDueDate(),
      invoiceNotes: '',
    });
  }, [show]);

  const amount = Number(fields.invoiceAmount);
  const canSubmit =
    Number.isFinite(amount) &&
    amount > 0 &&
    fields.invoiceDueDate &&
    fields.invoiceNotes.trim() &&
    !createInvoice.isLoading;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    createInvoice.mutate(
      {
        invoiceAmount: amount,
        invoiceDueDate: fields.invoiceDueDate,
        invoiceNotes: fields.invoiceNotes.trim(),
      },
      {
        onError: (err) =>
          toast.error(mutationErrorMessage(err, 'Failed to create invoice.')),
      },
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Create invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-3">
            <DisplayStyled>
              <label>Amount (USD)</label>
              <Form.Control
                autoFocus
                required
                min="0.01"
                step="0.01"
                type="number"
                value={fields.invoiceAmount}
                onChange={(e) =>
                  setFields((f) => ({
                    ...f,
                    invoiceAmount: e.target.value,
                  }))
                }
              />
            </DisplayStyled>
            <DisplayStyled>
              <label>Due Date</label>
              <Form.Control
                required
                type="date"
                value={fields.invoiceDueDate}
                onChange={(e) =>
                  setFields((f) => ({
                    ...f,
                    invoiceDueDate: e.target.value,
                  }))
                }
              />
            </DisplayStyled>
            <DisplayStyled>
              <label>Description</label>
              <Form.Control
                required
                as="textarea"
                rows={3}
                value={fields.invoiceNotes}
                onChange={(e) =>
                  setFields((f) => ({
                    ...f,
                    invoiceNotes: e.target.value,
                  }))
                }
              />
            </DisplayStyled>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={createInvoice.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!canSubmit}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Plus size={15} className="me-1" />
            {createInvoice.isLoading ? 'Creating...' : 'Create invoice'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const csvEscape = (value) => {
  const stringValue = valueForEvidence(value).replace(/\r?\n/g, ' ');
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const buildPaymentPlanAuthorizationEvidenceRows = (plan = {}) => {
  const metadata = plan.metadata || {};
  const authorization = metadata.paymentPlanAuthorization || {};
  const signature = authorization.signature || {};
  const cancellationContact = authorization.cancellationContact || {};
  const schedule = authorization.schedule || {};
  const rows = [];

  const add = (section, field, value) => {
    rows.push({ section, field, value: valueForEvidence(value) });
  };

  add('Payment plan', 'Payment plan ID', plan.paymentPlanId);
  add('Payment plan', 'Claim ID', plan.claimId);
  add('Payment plan', 'User ID', plan.userId);
  add('Payment plan', 'Status', plan.status);
  add('Payment plan', 'Term months', plan.termMonths);
  add('Payment plan', 'Total amount', plan.totalAmount);
  add('Payment plan', 'Down payment amount', plan.downPaymentAmount);
  add('Payment plan', 'Remaining amount', plan.remainingAmount);
  add('Payment plan', 'Source invoice IDs', plan.sourceInvoiceIds || []);
  add('Stripe', 'Stripe customer ID', plan.stripeCustomerId);
  add('Stripe', 'Stripe checkout session ID', plan.stripeCheckoutSessionId);
  add(
    'Stripe',
    'Stripe down payment intent ID',
    plan.stripeDownPaymentIntentId,
  );
  add('Stripe', 'Saved payment method ID', plan.stripeDefaultPaymentMethodId);
  add('Stripe', 'Payment method brand', plan.paymentMethodBrand);
  add('Stripe', 'Payment method last 4', plan.paymentMethodLast4);

  add('Authorization', 'Accepted', authorization.accepted);
  add('Authorization', 'Terms version', authorization.termsVersion);
  add('Authorization', 'Terms reviewed', authorization.termsReviewed);
  add('Authorization', 'Acceptance method', authorization.acceptanceMethod);
  add('Authorization', 'Accepted at server', authorization.acceptedAt);
  add('Authorization', 'Accepted at client', authorization.acceptedAtClient);
  add('Authorization', 'Accepted by user ID', authorization.acceptedByUserId);
  add('Authorization', 'Accepted by email', authorization.acceptedByEmail);
  add('Authorization', 'Typed signature type', signature.type);
  add('Authorization', 'Typed signature value', signature.value);
  add('Authorization', 'IP address', authorization.ipAddress);
  add('Authorization', 'User agent', authorization.userAgent);
  add(
    'Authorization',
    'Invalidated at',
    metadata.paymentPlanAuthorizationInvalidatedAt,
  );
  add('Authorization', 'Cancellation email', cancellationContact.email);
  add('Authorization', 'Cancellation phone', cancellationContact.phone);

  Object.entries(authorization.acknowledgments || {}).forEach(
    ([key, value]) => {
      add('Acknowledgment', key, value);
    },
  );

  add('Authorized schedule', 'Total amount', schedule.totalAmount);
  add('Authorized schedule', 'Down payment amount', schedule.downPaymentAmount);
  add('Authorized schedule', 'Remaining amount', schedule.remainingAmount);
  add('Authorized schedule', 'Term months', schedule.termMonths);
  (schedule.installments || []).forEach((installment) => {
    add(
      'Authorized schedule',
      `Installment ${installment.sequence}`,
      `${installment.dueDate || 'no due date'} | ${installment.amount || formatCents(installment.amountCents || 0)}`,
    );
  });

  (plan.installments || []).forEach((installment) => {
    add(
      'Actual installment',
      `Sequence ${installment.sequence}`,
      [
        `status=${installment.status}`,
        `dueDate=${installment.dueDate || '--'}`,
        `amount=${installment.amount || formatCents(installment.amountCents || 0)}`,
        `stripeInvoiceId=${installment.stripeInvoiceId || '--'}`,
        `stripePaymentIntentId=${installment.stripePaymentIntentId || '--'}`,
        `stripeCheckoutSessionId=${installment.stripeCheckoutSessionId || '--'}`,
        `paidAt=${installment.paidAt || '--'}`,
        `failedAt=${installment.failedAt || '--'}`,
      ].join(' | '),
    );
  });

  add('Raw authorization', 'JSON', authorization);

  return rows;
};

const buildPaymentPlanAuthorizationCsv = (plan) => {
  const rows = buildPaymentPlanAuthorizationEvidenceRows(plan);
  return [
    ['section', 'field', 'value'].map(csvEscape).join(','),
    ...rows.map((row) =>
      [row.section, row.field, row.value].map(csvEscape).join(','),
    ),
  ].join('\n');
};

const downloadTextFile = ({ content, filename, type }) => {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const PRESET_TERMS = [3, 6, 9, 12];
const PRESET_DEPOSIT_PERCENTS = [10, 20, 25, 50];

const PaymentPlanModal = ({
  show,
  onHide,
  claimId,
  isAdmin,
  summary,
  refetch,
  isFetching,
  existingPlan = null,
  onSaved,
}) => {
  const { user } = useUser();
  const isEditingSetup = Boolean(existingPlan?.paymentPlanId);
  const balanceCents = isEditingSetup
    ? Number(existingPlan?.totalCents || 0)
    : Number(summary?.balance?.balanceCents || 0);
  const eligibleInvoiceCount = isEditingSetup
    ? existingPlan?.sourceInvoiceIds?.length || 0
    : summary?.balance?.eligibleInvoices?.length || 0;
  const hasBalance = balanceCents > 0;
  const defaults = summary?.defaults || {};
  const adminMinTerm = defaults.minTermMonths || 1;
  const adminMaxTerm = defaults.maxTermMonths || 24;
  const clientMinTerm = 1;
  const clientMaxTerm = 6;
  const minTerm = isAdmin ? adminMinTerm : clientMinTerm;
  const maxTerm = isAdmin ? adminMaxTerm : clientMaxTerm;
  const defaultDepositPercent = defaults.downPaymentPercent || 10;
  const clientTerms = useMemo(
    () =>
      defaults.clientTerms?.length ? defaults.clientTerms : [1, 2, 3, 4, 5, 6],
    [defaults.clientTerms],
  );
  const billingDate =
    summary?.balance?.asOfDate && dayjs(summary.balance.asOfDate).isValid()
      ? dayjs(summary.balance.asOfDate)
      : dayjs(existingPlan?.installments?.[0]?.dueDate || undefined);
  const minimumStartDate = billingDate.format('YYYY-MM-DD');
  // Staff get the extended 3-month window; edit mode also grandfathers a
  // stored first-installment date beyond the viewer's own window (matches
  // the server's allowThroughDate rule).
  const windowMaxDate = getLatestFirstInstallmentDate(billingDate, {
    extendedWindow: isAdmin,
  });
  const storedFirstInstallmentDueDate = (existingPlan?.installments || []).find(
    (installment) => Number(installment.sequence) === 1,
  )?.dueDate;
  const latestFirstInstallmentDate =
    storedFirstInstallmentDueDate &&
    dayjs(storedFirstInstallmentDueDate).isAfter(dayjs(windowMaxDate))
      ? dayjs(storedFirstInstallmentDueDate).format('YYYY-MM-DD')
      : windowMaxDate;
  const defaultFirstInstallmentDate =
    getDefaultFirstInstallmentDate(billingDate);

  const initialTerm = existingPlan?.termMonths || (isAdmin ? 3 : 3);
  const [termMonths, setTermMonths] = useState(initialTerm);
  const [downPaymentCents, setDownPaymentCents] = useState(0);
  const [downPaymentInput, setDownPaymentInput] = useState('0.00');
  const [firstInstallmentDate, setFirstInstallmentDate] = useState(
    defaultFirstInstallmentDate,
  );
  const [customInstallmentDates, setCustomInstallmentDates] = useState([]);
  const [note, setNote] = useState('');
  const [authorizationAcknowledgments, setAuthorizationAcknowledgments] =
    useState(() => createPaymentPlanAuthorizationState());
  const [typedSignature, setTypedSignature] = useState('');
  const signerName = getPaymentPlanAuthorizationSignerName(user);

  // Reset state each time the modal opens with a fresh balance.
  useEffect(() => {
    if (!show) return;
    const existingFutureInstallments = (existingPlan?.installments || [])
      .filter((installment) => Number(installment.sequence) > 0)
      .sort((a, b) => Number(a.sequence) - Number(b.sequence));
    const existingFirstDate = existingFutureInstallments[0]?.dueDate;
    const defaultDownPaymentCents = isEditingSetup
      ? Number(existingPlan?.downPaymentCents || 0)
      : Math.round((balanceCents * defaultDepositPercent) / 100);
    const nextTerm = clamp(
      Number(existingPlan?.termMonths || (isAdmin ? 3 : 3)),
      minTerm,
      maxTerm,
    );
    const nextFirstInstallmentDate =
      existingFirstDate || defaultFirstInstallmentDate;
    setTermMonths(nextTerm);
    setDownPaymentCents(defaultDownPaymentCents);
    setDownPaymentInput((defaultDownPaymentCents / 100).toFixed(2));
    setFirstInstallmentDate(nextFirstInstallmentDate);
    setCustomInstallmentDates(
      existingFutureInstallments.length
        ? existingFutureInstallments.map((installment) => installment.dueDate)
        : Array.from({ length: nextTerm }, (_, index) =>
            dayjs(nextFirstInstallmentDate)
              .add(index, 'month')
              .format('YYYY-MM-DD'),
          ),
    );
    setNote('');
    setAuthorizationAcknowledgments(createPaymentPlanAuthorizationState());
    setTypedSignature(signerName);
  }, [
    show,
    balanceCents,
    defaultDepositPercent,
    defaultFirstInstallmentDate,
    existingPlan,
    isAdmin,
    isEditingSetup,
    minTerm,
    maxTerm,
    signerName,
  ]);

  const adjustedTerm = clamp(termMonths, minTerm, maxTerm);

  useEffect(() => {
    if (!show || !isAdmin) return;
    setCustomInstallmentDates((previousDates) =>
      Array.from(
        { length: adjustedTerm },
        (_, index) =>
          previousDates[index] ||
          dayjs(firstInstallmentDate).add(index, 'month').format('YYYY-MM-DD'),
      ),
    );
  }, [show, isAdmin, adjustedTerm, firstInstallmentDate]);

  const installmentDueDatesForSchedule = useMemo(() => {
    if (!isAdmin) return undefined;
    return Array.from(
      { length: adjustedTerm },
      (_, index) =>
        customInstallmentDates[index] ||
        dayjs(firstInstallmentDate).add(index, 'month').format('YYYY-MM-DD'),
    );
  }, [isAdmin, adjustedTerm, customInstallmentDates, firstInstallmentDate]);

  const schedule = useMemo(
    () =>
      computeSchedule({
        balanceCents,
        termMonths: adjustedTerm,
        downPaymentCents,
        firstInstallmentDate,
        installmentDueDates: installmentDueDatesForSchedule,
      }),
    [
      balanceCents,
      adjustedTerm,
      downPaymentCents,
      firstInstallmentDate,
      installmentDueDatesForSchedule,
    ],
  );

  const startPlan = useStartPaymentPlan(claimId, (result) => {
    if (isAdmin) {
      toast.success(
        'Payment plan set up. The client can complete the down payment from their billing tab.',
      );
    } else {
      toast.success(
        'Payment plan setup started. Opening down-payment checkout.',
      );
      openCheckoutUrl(result?.checkoutSession?.url);
    }
    onHide();
  });
  const updatePlanSetup = useUpdatePaymentPlanSetup(claimId, (result) => {
    if (!isAdmin && result?.checkoutSession?.url) {
      toast.success('Payment plan terms updated. Opening Stripe checkout.');
      openCheckoutUrl(result.checkoutSession.url);
      onSaved?.(result);
      onHide();
      return;
    }
    toast.success('Payment plan terms updated.');
    onSaved?.(result);
    onHide();
  });

  const submit = (mutation, payload, fallback) => {
    mutation.mutate(payload, {
      onError: (err) => toast.error(mutationErrorMessage(err, fallback)),
    });
  };

  const payload = () => ({
    termMonths: adjustedTerm,
    downPaymentAmount: (downPaymentCents / 100).toFixed(2),
    firstInstallmentDate,
    installmentDueDates: installmentDueDatesForSchedule,
    note: note?.trim() ? note.trim() : undefined,
    paymentPlanAuthorization: isAdmin
      ? undefined
      : buildPaymentPlanAuthorizationPayload({
          acknowledgments: authorizationAcknowledgments,
          typedSignature,
          schedule,
          supportContact: summary?.supportContact,
        }),
  });

  const handleClientStart = () => {
    if (
      !isPaymentPlanAuthorizationComplete(
        authorizationAcknowledgments,
        typedSignature,
      )
    ) {
      toast.error(
        'Review and accept the payment plan authorization before continuing.',
      );
      return;
    }
    if (isEditingSetup) {
      submit(
        updatePlanSetup,
        { paymentPlanId: existingPlan.paymentPlanId, ...payload() },
        'Failed to update payment plan terms.',
      );
      return;
    }
    submit(startPlan, payload(), 'Failed to start payment plan setup.');
  };

  const handleAdminStart = () => {
    if (isEditingSetup) {
      submit(
        updatePlanSetup,
        { paymentPlanId: existingPlan.paymentPlanId, ...payload() },
        'Failed to update payment plan terms.',
      );
      return;
    }
    submit(startPlan, payload(), 'Failed to start payment plan setup.');
  };

  const depositPercent =
    balanceCents > 0 ? Math.round((downPaymentCents / balanceCents) * 100) : 0;
  const isZeroDownPayment = downPaymentCents === 0;
  const submitting = startPlan.isLoading || updatePlanSetup.isLoading;
  const authorizationComplete =
    isAdmin ||
    isPaymentPlanAuthorizationComplete(
      authorizationAcknowledgments,
      typedSignature,
    );
  const firstInstallmentLabel = firstInstallmentDate
    ? dayjs(firstInstallmentDate).format('MMM D, YYYY')
    : 'Select date';
  const handleAuthorizationAcknowledgmentChange = (key, checked) => {
    setAuthorizationAcknowledgments((previous) => ({
      ...previous,
      [key]: checked,
    }));
  };
  const setDownPaymentFromCents = (cents) => {
    const clampedCents = clamp(Math.round(cents || 0), 0, balanceCents);
    setDownPaymentCents(clampedCents);
    setDownPaymentInput((clampedCents / 100).toFixed(2));
  };
  const handleDownPaymentInputChange = (event) => {
    const next = event.target.value;
    setDownPaymentInput(next);
    const dollars = Number(next);
    if (!Number.isFinite(dollars) || dollars < 0) return;
    setDownPaymentCents(clamp(Math.round(dollars * 100), 0, balanceCents));
  };
  const handleDownPaymentInputBlur = () => {
    setDownPaymentFromCents(downPaymentCents);
  };
  const handleFirstInstallmentDateChange = (nextDate) => {
    setFirstInstallmentDate(nextDate);
    if (!isAdmin || !nextDate) return;
    setCustomInstallmentDates(
      Array.from({ length: adjustedTerm }, (_, index) =>
        dayjs(nextDate).add(index, 'month').format('YYYY-MM-DD'),
      ),
    );
  };
  const handleCustomInstallmentDateChange = (index, nextDate) => {
    setCustomInstallmentDates((previousDates) =>
      Array.from({ length: adjustedTerm }, (_, currentIndex) =>
        currentIndex === index
          ? nextDate
          : previousDates[currentIndex] ||
            dayjs(firstInstallmentDate)
              .add(currentIndex, 'month')
              .format('YYYY-MM-DD'),
      ),
    );
    if (index === 0) setFirstInstallmentDate(nextDate);
  };

  return (
    <>
      <PaymentPlanModalGlobalStyle />
      <Modal
        show={show}
        onHide={onHide}
        centered
        backdrop="static"
        dialogClassName="billing-plan-modal"
        keyboard
      >
        <ModalHero>
          <button
            type="button"
            className="close-btn"
            onClick={onHide}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="hero-row">
            <div>
              <span className="hero-eyebrow">
                <Sparkles size={11} />
                {isEditingSetup
                  ? 'Edit payment plan'
                  : isAdmin
                    ? 'New payment plan'
                    : 'Set up payment plan'}
              </span>
              <h3 className="hero-title">
                {isEditingSetup
                  ? 'Adjust the payment plan terms'
                  : isAdmin
                    ? 'Set up a flexible payment plan'
                    : 'Spread your balance over monthly payments'}
              </h3>
              <p className="hero-sub">
                {isEditingSetup
                  ? 'Update the term, down payment, and installment schedule before the client starts the plan.'
                  : isAdmin
                    ? 'Choose any term and any down payment. The client will complete the setup from their billing tab to activate the plan.'
                    : 'Pick a term and a down payment that works for you. You’ll complete the setup in Stripe now to activate the plan.'}
              </p>
            </div>
            <div className="hero-balance">
              <div className="label">
                {isEditingSetup ? 'Plan balance' : 'Eligible balance'}
              </div>
              <div className="value">{formatCents(balanceCents)}</div>
              <div className="meta">
                {`${eligibleInvoiceCount} invoice${eligibleInvoiceCount === 1 ? '' : 's'}`}
              </div>
            </div>
          </div>
        </ModalHero>

        <Modal.Body style={{ padding: '1.25rem 1.6rem 0.6rem' }}>
          {!hasBalance ? (
            <Alert
              variant="light"
              className="mb-0"
              style={{ background: '#f7f8fb', border: '1px dashed #d9e0ea' }}
            >
              <strong>No eligible balance.</strong>{' '}
              {isAdmin
                ? 'No open or draft invoices qualify for a payment plan right now.'
                : 'Once you have open invoices that are at least 60 days old, you’ll be able to set up a payment plan here.'}
            </Alert>
          ) : (
            <>
              <PhaseSection data-variant="today">
                <PhaseHeader>
                  <PhaseEyebrow $variant="today">
                    <span className="phase-step">1</span>
                    <Clock3 size={11} />
                    Today · Stripe checkout
                  </PhaseEyebrow>
                  <PhaseAside>
                    {isZeroDownPayment
                      ? 'No charge today'
                      : `${depositPercent}% down`}
                  </PhaseAside>
                </PhaseHeader>
                <PhaseBody>
                  <ControlBlock>
                    <div className="control-label">
                      <span className="label">
                        <Wallet
                          size={13}
                          style={{ verticalAlign: '-2px', marginRight: 4 }}
                        />
                        Down payment
                      </span>
                      <span className="value">
                        {formatCents(schedule?.downPaymentCents || 0)}
                      </span>
                    </div>
                    <InlineMoneyEditor>
                      <span>$</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        max={balanceCents / 100}
                        value={downPaymentInput}
                        onChange={handleDownPaymentInputChange}
                        onBlur={handleDownPaymentInputBlur}
                        aria-label="Down payment amount charged today"
                      />
                    </InlineMoneyEditor>
                    <TermChips>
                      {PRESET_DEPOSIT_PERCENTS.map((pct) => (
                        <Chip
                          key={pct}
                          type="button"
                          $active={depositPercent === pct}
                          onClick={() =>
                            setDownPaymentFromCents(
                              Math.round((balanceCents * pct) / 100),
                            )
                          }
                        >
                          {pct}%
                        </Chip>
                      ))}
                      <Chip
                        type="button"
                        $active={isZeroDownPayment}
                        onClick={() => setDownPaymentFromCents(0)}
                      >
                        $0
                      </Chip>
                    </TermChips>
                  </ControlBlock>
                  <PhaseFootnote $variant="today">
                    <CreditCard size={14} />
                    {isZeroDownPayment
                      ? 'Billing method will be saved in Stripe checkout — no charge today.'
                      : `Charged immediately at Stripe checkout — ${formatCents(schedule?.downPaymentCents || 0)} today.`}
                  </PhaseFootnote>
                </PhaseBody>
              </PhaseSection>

              <PhaseSection>
                <PhaseHeader>
                  <PhaseEyebrow $variant="future">
                    <span className="phase-step">2</span>
                    <CalendarDays size={11} />
                    Future schedule · auto-charged monthly
                  </PhaseEyebrow>
                  <PhaseAside>
                    {`${adjustedTerm} × ${formatCents(schedule?.monthlyTopCents || 0)}`}
                  </PhaseAside>
                </PhaseHeader>
                <PhaseBody>
                  <ControlGrid
                    style={{
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(220px, 1fr))',
                    }}
                  >
                    <ControlBlock>
                      <div className="control-label">
                        <span className="label">Term</span>
                        <span className="value">{adjustedTerm} mo</span>
                      </div>
                      {isAdmin ? (
                        <TermChips>
                          {PRESET_TERMS.map((t) => (
                            <Chip
                              key={t}
                              type="button"
                              $active={adjustedTerm === t}
                              onClick={() => setTermMonths(t)}
                            >
                              {t} mo
                            </Chip>
                          ))}
                        </TermChips>
                      ) : null}
                      <input
                        type="range"
                        min={minTerm}
                        max={maxTerm}
                        step={1}
                        value={adjustedTerm}
                        onChange={(event) =>
                          setTermMonths(Number(event.target.value))
                        }
                      />
                      {!isAdmin ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            color: '#7a8699',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            marginTop: '0.25rem',
                          }}
                        >
                          {clientTerms.map((term) => (
                            <span key={term}>{term}</span>
                          ))}
                        </div>
                      ) : null}
                    </ControlBlock>

                    <ControlBlock>
                      <div className="control-label">
                        <span className="label">
                          <CalendarDays
                            size={13}
                            style={{ verticalAlign: '-2px', marginRight: 4 }}
                          />
                          First Installment Date
                        </span>
                        <span className="value">{firstInstallmentLabel}</span>
                      </div>
                      <Form.Control
                        type="date"
                        min={minimumStartDate}
                        max={latestFirstInstallmentDate}
                        value={firstInstallmentDate}
                        onChange={(event) =>
                          handleFirstInstallmentDateChange(event.target.value)
                        }
                        aria-label="Date of the first future installment"
                      />
                      <ControlHint>
                        {isAdmin
                          ? `Installment 1 can be any date through ${dayjs(windowMaxDate).format('MMMM D, YYYY')}.`
                          : latestFirstInstallmentDate !== windowMaxDate
                            ? `This plan's installment 1 is scheduled for ${dayjs(latestFirstInstallmentDate).format('MMMM D, YYYY')}. You can keep that date or choose an earlier one.`
                            : "Installment 1 must be within 30 days or by the 1st of next month, whichever is later. If the next 1st is two weeks or less away, the following month's 1st is allowed too."}
                      </ControlHint>
                    </ControlBlock>
                  </ControlGrid>
                  <PhaseFootnote>
                    <ShieldCheck size={14} />
                    {`Auto-charged to the saved billing method on each due date · ${formatCents(schedule?.remainingCents || 0)} total across ${adjustedTerm} installment${adjustedTerm === 1 ? '' : 's'}.`}
                  </PhaseFootnote>
                </PhaseBody>
              </PhaseSection>

              {schedule ? (
                <>
                  <SchedulePreviewHeading>
                    <span className="heading">Plan preview</span>
                    <span className="hint">
                      {formatCents(schedule.totalCents)} total
                    </span>
                  </SchedulePreviewHeading>
                  <ScheduleList>
                    <ScheduleHeader>
                      <div>#</div>
                      <div>Installment</div>
                      <div>Due</div>
                      <div
                        className="col-amount"
                        style={{ textAlign: 'right' }}
                      >
                        Amount
                      </div>
                    </ScheduleHeader>
                    <ScheduleRow $isDeposit>
                      <div className="row-num" aria-label="Today">
                        <Clock3 size={13} />
                      </div>
                      <div>
                        <div className="row-label">
                          {isZeroDownPayment
                            ? 'Billing method setup'
                            : 'Down payment'}
                        </div>
                        <div className="row-status">
                          {isZeroDownPayment
                            ? 'No charge today — billing method only'
                            : 'Charged in Stripe checkout'}
                        </div>
                      </div>
                      <div className="row-date">Due today</div>
                      <div className="row-amount">
                        {formatCents(schedule.downPaymentCents)}
                      </div>
                    </ScheduleRow>
                    {(schedule.installments || []).map((installment, idx) => (
                      <ScheduleRow key={idx}>
                        <div className="row-num">{idx + 1}</div>
                        <div>
                          <div className="row-label">Installment {idx + 1}</div>
                          <div className="row-status">
                            Auto-charge to saved billing method
                          </div>
                        </div>
                        <div className="row-date">
                          {isAdmin ? (
                            <Form.Control
                              size="sm"
                              type="date"
                              min={minimumStartDate}
                              max={
                                idx === 0
                                  ? latestFirstInstallmentDate
                                  : undefined
                              }
                              value={
                                installmentDueDatesForSchedule?.[idx] ||
                                installment.dueDate
                              }
                              onChange={(event) =>
                                handleCustomInstallmentDateChange(
                                  idx,
                                  event.target.value,
                                )
                              }
                            />
                          ) : (
                            dayjs(installment.dueDate).format('MMM D, YYYY')
                          )}
                        </div>
                        <div className="row-amount">
                          {formatCents(installment.amountCents)}
                        </div>
                      </ScheduleRow>
                    ))}
                  </ScheduleList>
                  {!isAdmin ? (
                    <PaymentPlanAuthorizationTerms
                      acknowledgments={authorizationAcknowledgments}
                      onAcknowledgmentChange={
                        handleAuthorizationAcknowledgmentChange
                      }
                      typedSignature={typedSignature}
                      onTypedSignatureChange={setTypedSignature}
                      schedule={schedule}
                      signerName={signerName}
                      signerEmail={user?.userEmail}
                      supportContact={summary?.supportContact}
                      disabled={submitting || isFetching}
                    />
                  ) : null}
                </>
              ) : null}

              <Form.Control
                className="mt-3"
                as="textarea"
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={
                  isAdmin
                    ? 'Optional setup note (visible to the client)'
                    : 'Optional note'
                }
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: '1px solid #eef2f7',
            padding: '0.85rem 1.6rem',
            background: '#fafbfd',
          }}
        >
          {/* <Button
            variant="link"
            onClick={() => refetch?.()}
            disabled={isFetching}
            className="me-auto"
            style={{ color: '#6b7689', textDecoration: 'none' }}
          >
            <RefreshCcw size={14} className="me-1" />
            Refresh eligibility
          </Button> */}
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={submitting}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            Cancel
          </Button>
          {isAdmin ? (
            <Button
              variant="primary"
              disabled={
                !hasBalance || !schedule || !firstInstallmentDate || submitting
              }
              onClick={handleAdminStart}
              style={{
                borderRadius: 10,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ShieldCheck size={16} className="me-1" />
              {isEditingSetup ? 'Save terms' : 'Set up plan'}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={
                !hasBalance ||
                !schedule ||
                !firstInstallmentDate ||
                !authorizationComplete ||
                submitting
              }
              onClick={handleClientStart}
              style={{
                borderRadius: 10,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CreditCard size={16} className="me-1" />
              {isEditingSetup
                ? 'Save terms'
                : isZeroDownPayment
                  ? 'Continue to billing method setup'
                  : 'Continue to down payment'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

const PaymentPlanAuthorizationEvidenceModal = ({ show, onHide, plan }) => {
  const [activeTab, setActiveTab] = useState('evidence');
  const authorization = plan?.metadata?.paymentPlanAuthorization || null;
  const evidenceRows = useMemo(
    () =>
      authorization ? buildPaymentPlanAuthorizationEvidenceRows(plan) : [],
    [authorization, plan],
  );
  const termsSections = useMemo(
    () =>
      authorization
        ? buildPaymentPlanAuthorizationTermsSections({
            schedule: authorization.schedule,
            supportContact: authorization.cancellationContact,
          })
        : [],
    [authorization],
  );
  const keyEvidenceRows = evidenceRows.filter(
    (row) =>
      row.section === 'Authorization' ||
      row.section === 'Payment plan' ||
      row.section === 'Stripe',
  );

  if (!authorization) return null;

  const handleDownloadCsv = () => {
    downloadTextFile({
      content: buildPaymentPlanAuthorizationCsv(plan),
      filename: `payment-plan-authorization-${plan.paymentPlanId}.csv`,
      type: 'text/csv;charset=utf-8',
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      dialogClassName="billing-plan-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 750 }}>
          Payment authorization evidence
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.75rem',
            alignItems: 'flex-start',
            marginBottom: '1rem',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: '#166534',
                background: '#ecfdf5',
                border: '1px solid #bbf7d0',
                borderRadius: 999,
                padding: '0.2rem 0.55rem',
                fontSize: '0.76rem',
                fontWeight: 800,
                marginBottom: '0.45rem',
              }}
            >
              <ShieldCheck size={13} />
              Accepted authorization
            </div>
            <p
              style={{
                margin: 0,
                color: '#64748b',
                fontSize: '0.88rem',
                lineHeight: 1.45,
              }}
            >
              This record includes the customer acceptance, typed signature,
              timestamps, request context, authorized schedule, and related
              Stripe IDs.
            </p>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownloadCsv}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 9,
              fontWeight: 700,
              flex: '0 0 auto',
            }}
          >
            <Download size={14} className="me-1" />
            Download CSV
          </Button>
        </div>

        <AuthorizationModalTabs
          role="tablist"
          aria-label="Payment authorization details"
        >
          <AuthorizationModalTab
            type="button"
            $active={activeTab === 'evidence'}
            onClick={() => setActiveTab('evidence')}
            role="tab"
            aria-selected={activeTab === 'evidence'}
          >
            Evidence
          </AuthorizationModalTab>
          <AuthorizationModalTab
            type="button"
            $active={activeTab === 'terms'}
            onClick={() => setActiveTab('terms')}
            role="tab"
            aria-selected={activeTab === 'terms'}
          >
            Terms text
          </AuthorizationModalTab>
        </AuthorizationModalTabs>

        {activeTab === 'evidence' ? (
          <>
            <AuthorizationEvidenceGrid>
              <AuthorizationEvidenceTile>
                <div className="label">Accepted by</div>
                <div className="value">
                  {valueForEvidence(authorization.acceptedByEmail)}
                </div>
              </AuthorizationEvidenceTile>
              <AuthorizationEvidenceTile>
                <div className="label">Accepted at</div>
                <div className="value">
                  {formatAuthorizationTimestampForModal(
                    authorization.acceptedAt,
                  )}
                </div>
              </AuthorizationEvidenceTile>
              <AuthorizationEvidenceTile>
                <div className="label">Typed signature</div>
                <div className="value">
                  {valueForEvidence(authorization.signature?.value)}
                </div>
              </AuthorizationEvidenceTile>
              <AuthorizationEvidenceTile>
                <div className="label">Terms version</div>
                <div className="value">
                  {valueForEvidence(authorization.termsVersion)}
                </div>
              </AuthorizationEvidenceTile>
            </AuthorizationEvidenceGrid>

            <AuthorizationEvidenceSection>
              <div className="section-title">Evidence fields</div>
              <AuthorizationEvidenceTable>
                {keyEvidenceRows.map((row) => (
                  <div className="row" key={`${row.section}-${row.field}`}>
                    <div className="key">{row.field}</div>
                    <div className="value">
                      {valueForAuthorizationModal(row)}
                    </div>
                  </div>
                ))}
              </AuthorizationEvidenceTable>
            </AuthorizationEvidenceSection>

            <AuthorizationEvidenceSection>
              <div className="section-title">Authorized schedule</div>
              <AuthorizationEvidenceTable>
                {evidenceRows
                  .filter((row) => row.section === 'Authorized schedule')
                  .map((row) => (
                    <div className="row" key={`${row.section}-${row.field}`}>
                      <div className="key">{row.field}</div>
                      <div className="value">{row.value}</div>
                    </div>
                  ))}
              </AuthorizationEvidenceTable>
            </AuthorizationEvidenceSection>

            <AuthorizationEvidenceSection>
              <div className="section-title">Raw authorization record</div>
              <AuthorizationRawJson>
                {JSON.stringify(authorization, null, 2)}
              </AuthorizationRawJson>
            </AuthorizationEvidenceSection>
          </>
        ) : (
          <AuthorizationTermsViewer>
            <div className="terms-version">
              Terms version: {valueForEvidence(authorization.termsVersion)}
            </div>
            {termsSections.map((section) => (
              <div className="terms-section" key={section.title}>
                <div className="terms-title">{section.title}</div>
                <p className="terms-body">{section.body}</p>
              </div>
            ))}
          </AuthorizationTermsViewer>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleDownloadCsv}>
          <Download
            size={14}
            className="me-1"
            style={{ display: 'flex', alignItems: 'center' }}
          />
          Download CSV
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ActivePlanCard = ({
  claimId,
  isAdmin,
  plan,
  refetch,
  supportContact,
  summary,
  // Embedded = rendered inside an invoice tile's expandable plan panel:
  // flatten the outer card chrome so it reads as part of the tile.
  embedded = false,
}) => {
  const [note, setNote] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);

  const approvePlan = useApprovePaymentPlan(claimId, () => {
    toast.success(
      'Payment plan approved. The client can complete the down payment from their billing tab.',
    );
  });
  const rejectPlan = useRejectPaymentPlan(claimId, () => {
    toast.success('Payment plan request rejected.');
    setNote('');
  });
  const billingMethodSession = useCreatePaymentPlanBillingMethodSession(
    claimId,
    (result) => {
      openCheckoutUrl(result?.checkoutSession?.url);
    },
  );
  const cancelPlan = useCancelPaymentPlan(claimId, () => {
    toast.success('Payment plan cancelled.');
    setShowCancelModal(false);
    setCancelNote('');
    refetch?.();
  });

  const submit = (mutation, payload, fallback) => {
    mutation.mutate(payload, {
      onError: (err) => toast.error(mutationErrorMessage(err, fallback)),
    });
  };

  const depositProcessing = isPaymentPlanDepositProcessing(plan);
  const meta = depositProcessing
    ? { label: 'Bank payment processing', Icon: Clock3 }
    : PLAN_STATUS[plan.status] || PLAN_STATUS.requested;
  const Icon = meta.Icon;
  const checkoutUrl = plan.metadata?.checkoutUrl;
  const hasPaymentAuthorization = Boolean(
    plan.metadata?.paymentPlanAuthorization?.accepted,
  );
  const paymentMethodLabel =
    plan.paymentMethodBrand && plan.paymentMethodLast4
      ? `${plan.paymentMethodBrand.toUpperCase()} ending ${plan.paymentMethodLast4}`
      : 'No saved billing method yet';
  const depositPercent =
    Number(plan.totalCents) > 0
      ? (Number(plan.downPaymentCents) / Number(plan.totalCents)) * 100
      : 0;
  const canCancelPlan =
    PAYMENT_PLAN_CANCELLABLE_STATUSES.includes(plan.status) &&
    !depositProcessing;
  const supportEmail = supportContact?.email || 'billing@veritasratings.com';
  const supportPhone = supportContact?.phone || '(615) 263-4307';
  const supportPhoneHref = supportPhone.replace(/[^\d+]/g, '');

  const subtitle = (() => {
    switch (plan.status) {
      case 'requested':
        return isAdmin
          ? 'This older request is waiting for review. Approving it creates the Stripe setup checkout for the client.'
          : 'Your request was submitted. Our billing team will review it shortly — you’ll receive an email when it’s approved.';
      case 'setup_pending':
        if (depositProcessing) {
          return isAdmin
            ? 'The client submitted a bank payment. Wait for Stripe to confirm success or failure before changing the plan.'
            : 'Your bank payment was submitted. ACH payments can take a few business days to confirm.';
        }
        return isAdmin
          ? 'Plan is set up. The client needs to complete setup from their billing tab to activate the schedule.'
          : 'Complete setup in Stripe to activate the schedule and save your billing method.';
      case 'active':
        return 'Down payment complete. We’ll charge the saved billing method on each installment date.';
      case 'past_due':
        return 'A scheduled installment failed. Update the billing method to recover the plan.';
      case 'failed':
        return 'Recent installment attempts failed. Update the billing method to bring this plan back to active.';
      case 'completed':
        return 'All installments have been paid in full. Nothing left to do.';
      default:
        return 'Review this plan before taking the next billing action.';
    }
  })();

  const handleApprove = () =>
    submit(
      approvePlan,
      { paymentPlanId: plan.paymentPlanId, note: note?.trim() || undefined },
      'Failed to approve payment plan.',
    );
  const handleReject = () => {
    if (!window.confirm('Reject this payment plan request?')) return;
    submit(
      rejectPlan,
      { paymentPlanId: plan.paymentPlanId, note: note?.trim() || undefined },
      'Failed to reject payment plan.',
    );
  };
  const handleBillingMethod = () =>
    submit(
      billingMethodSession,
      { paymentPlanId: plan.paymentPlanId },
      'Failed to open billing method setup.',
    );
  const handleAdminCancel = () =>
    submit(
      cancelPlan,
      {
        paymentPlanId: plan.paymentPlanId,
        note: cancelNote?.trim() || undefined,
      },
      'Failed to cancel payment plan.',
    );

  return (
    <>
      <Card
        style={
          embedded
            ? {
                border: 'none',
                boxShadow: 'none',
                borderRadius: 0,
                margin: 0,
                background: '#fbfaff',
              }
            : undefined
        }
      >
        <CardBody>
          <div
            className="d-flex justify-content-between align-items-start mb-2"
            style={{ gap: '0.75rem', flexWrap: 'wrap' }}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#0f1c33',
                }}
              >
                <Icon size={18} />
                <strong style={{ fontSize: '1rem' }}>
                  {plan.termMonths}
                  -month plan
                </strong>
                <span style={{ color: '#7a8699', fontSize: '0.85rem' }}>
                  · {Math.round(depositPercent)}% down
                </span>
              </div>
              {plan.sourceInvoice?.label ? (
                // Per-invoice plans: it must NEVER be in question which
                // invoice a plan covers (Madison, 2026-07-30).
                <div
                  style={{
                    marginTop: '0.3rem',
                    color: '#4f46e5',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  For the {plan.sourceInvoice.label}
                </div>
              ) : null}
              <p
                style={{
                  margin: '0.4rem 0 0',
                  color: '#6b7689',
                  maxWidth: 640,
                  fontSize: '0.93rem',
                  lineHeight: 1.45,
                }}
              >
                {subtitle}
              </p>
            </div>
            <StatusBadge
              $variant={depositProcessing ? 'processing' : plan.status}
              style={{ textAlign: 'center' }}
            >
              {meta.label}
            </StatusBadge>
          </div>

          <SummaryGrid>
            <div className="stat">
              <div className="label">Total</div>
              <div className="value">{formatCurrency(plan.totalAmount)}</div>
            </div>
            <div className="stat">
              <div className="label">Down payment</div>
              <div className="value">
                {formatCurrency(plan.downPaymentAmount)}
              </div>
            </div>
            <div className="stat">
              <div className="label">Remaining</div>
              <div className="value">
                {formatCurrency(plan.remainingAmount)}
              </div>
            </div>
            <div className="stat">
              <div className="label">Billing method</div>
              <div className="value">{paymentMethodLabel}</div>
            </div>
          </SummaryGrid>

          {depositProcessing &&
          plan.metadata?.depositVerification?.verificationUrl ? (
            <Alert variant="warning" className="mt-3 mb-0">
              <strong>Bank account needs verification.</strong>{' '}
              {isAdmin
                ? 'Stripe sent a micro-deposit the client must confirm before the down payment can complete. Only the client can see the code on their bank statement.'
                : 'Stripe sent a small test deposit to your bank account. Confirm it to finish setting up your payment plan.'}{' '}
              <Alert.Link
                href={plan.metadata.depositVerification.verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Stripe&apos;s verification page
              </Alert.Link>
            </Alert>
          ) : depositProcessing ? (
            <Alert variant="info" className="mt-3 mb-0">
              <strong>Bank payment submitted.</strong>{' '}
              {isAdmin
                ? 'Do not edit terms or start another checkout while the ACH debit is processing. Check Stripe before taking any manual recovery action.'
                : 'No further action is needed right now. We will activate the payment plan after Stripe confirms the payment.'}
            </Alert>
          ) : plan.status === 'setup_pending' &&
            plan.metadata?.depositStatus === 'failed' ? (
            <Alert variant="warning" className="mt-3 mb-0">
              <strong>Previous payment attempt failed.</strong>{' '}
              {plan.metadata?.depositFailureMessage ||
                'The down payment could not be completed.'}{' '}
              {isAdmin
                ? 'The client can use "Start payment plan" on their billing tab to retry with a different payment method.'
                : 'Use "Start payment plan" to retry with a different payment method.'}
            </Alert>
          ) : null}

          {isAdmin && plan.status === 'requested' ? (
            <>
              <Form.Control
                className="mt-3"
                as="textarea"
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Optional approval or rejection note for the client"
              />
              <ActionRow>
                <Button
                  variant="primary"
                  disabled={approvePlan.isLoading}
                  onClick={handleApprove}
                >
                  <ShieldCheck size={16} className="me-1" />
                  Approve for client setup
                </Button>
                <Button
                  variant="outline-danger"
                  disabled={rejectPlan.isLoading}
                  onClick={handleReject}
                >
                  Reject request
                </Button>
              </ActionRow>
            </>
          ) : null}

          {plan.status === 'setup_pending' &&
          checkoutUrl &&
          !isAdmin &&
          !depositProcessing ? (
            <ActionRow>
              <Button
                variant="primary"
                onClick={() => openCheckoutUrl(checkoutUrl)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <CreditCard size={16} className="me-1" />
                Open setup checkout
              </Button>
              {/* <Button variant="outline-secondary" onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center' }}>
              <RefreshCcw size={14} className="me-1" />
              Refresh status
            </Button> */}
            </ActionRow>
          ) : null}

          {plan.status === 'setup_pending' && isAdmin ? (
            <ActionRow>
              <Button
                variant="outline-primary"
                disabled={depositProcessing}
                onClick={() => setShowEditModal(true)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <CalendarDays size={14} className="me-1" />
                Edit terms
              </Button>
              {/* <Button variant="outline-secondary" onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center' }}>
              <RefreshCcw size={14} className="me-1" />
              Refresh status
            </Button> */}
              <Button
                variant="outline-danger"
                disabled={!canCancelPlan}
                onClick={() => setShowCancelModal(true)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <XCircle size={14} className="me-1" />
                Cancel plan
              </Button>
            </ActionRow>
          ) : null}

          {PAYMENT_PLAN_CLIENT_PAYOFF_STATUSES.includes(plan.status) &&
          !isAdmin ? (
            <ActionRow>
              <Button
                variant="outline-primary"
                disabled={billingMethodSession.isLoading}
                onClick={handleBillingMethod}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <CreditCard size={16} className="me-1" />
                Update billing method
              </Button>
              {/* <Button variant="outline-secondary" onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center' }}>
              <RefreshCcw size={14} className="me-1" />
              Refresh status
            </Button> */}
              <Button
                variant="outline-danger"
                onClick={() => setShowCancelModal(true)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <XCircle size={14} className="me-1" />
                Cancel plan
              </Button>
            </ActionRow>
          ) : null}

          {PAYMENT_PLAN_CLIENT_PAYOFF_STATUSES.includes(plan.status) &&
          isAdmin ? (
            <ActionRow>
              {/* <Button variant="outline-secondary" onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center' }}>
              <RefreshCcw size={14} className="me-1" />
              Refresh status
            </Button> */}
              <Button
                variant="outline-danger"
                disabled={!canCancelPlan || cancelPlan.isLoading}
                onClick={() => setShowCancelModal(true)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <XCircle size={14} className="me-1" />
                Cancel plan
              </Button>
            </ActionRow>
          ) : null}

          <ScheduleList style={{ marginTop: '1.1rem', maxHeight: 'none' }}>
            <ScheduleHeader>
              <div>#</div>
              <div>Installment</div>
              <div>Due</div>
              <div className="col-amount" style={{ textAlign: 'right' }}>
                Amount
              </div>
            </ScheduleHeader>
            {(plan.installments || []).map((installment) => {
              const isDeposit = installment.sequence === 0;
              const isPaid = installment.status === 'paid';
              return (
                <ScheduleRow
                  key={installment.paymentPlanInstallmentId}
                  $isDeposit={isDeposit}
                  $isPaid={isPaid}
                >
                  <div className="row-num">{installment.sequence + 1}</div>
                  <div>
                    <div className="row-label">
                      {isDeposit
                        ? 'Down payment'
                        : `Installment ${installment.sequence}`}
                    </div>
                    <div className="row-status">
                      {installmentStatusLabel(installment.status)}
                    </div>
                  </div>
                  <div className="row-date">
                    {dayjs(installment.dueDate).format('MMM D, YYYY')}
                  </div>
                  <div className="row-amount">
                    {formatCurrency(installment.amount)}
                  </div>
                </ScheduleRow>
              );
            })}
          </ScheduleList>
          {isAdmin && hasPaymentAuthorization ? (
            <AuthorizationCardFooter>
              <AuthorizationEvidenceButton
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAuthorizationModal(true)}
              >
                <FileText size={13} />
                Authorization
              </AuthorizationEvidenceButton>
            </AuthorizationCardFooter>
          ) : null}
        </CardBody>
      </Card>
      <PaymentPlanModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        claimId={claimId}
        isAdmin={isAdmin}
        summary={summary}
        refetch={refetch}
        existingPlan={plan}
        onSaved={() => refetch?.()}
      />
      <PaymentPlanModalGlobalStyle />
      <PaymentPlanAuthorizationEvidenceModal
        show={showAuthorizationModal}
        onHide={() => setShowAuthorizationModal(false)}
        plan={plan}
      />
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        dialogClassName="billing-plan-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700 }}>
            Cancel payment plan
          </Modal.Title>
        </Modal.Header>
        {isAdmin ? (
          <>
            <Modal.Body>
              <p style={{ marginTop: 0, color: '#475569', lineHeight: 1.5 }}>
                Cancelling stops future payment-plan charges and voids unpaid
                installment invoices in Stripe. Paid installments stay recorded.
              </p>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, color: '#475569' }}>
                  Internal note
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cancelNote}
                  onChange={(event) => setCancelNote(event.target.value)}
                  placeholder="Optional reason for the cancellation"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowCancelModal(false)}
              >
                Keep plan
              </Button>
              <Button
                variant="danger"
                disabled={cancelPlan.isLoading}
                onClick={handleAdminCancel}
              >
                Cancel plan
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Body>
              <p style={{ marginTop: 0, color: '#475569', lineHeight: 1.5 }}>
                To cancel this plan, contact the billing team.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-secondary"
                  href={`tel:${supportPhoneHref}`}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Phone size={14} className="me-1" />
                  {supportPhone}
                </Button>
                <Button
                  variant="outline-secondary"
                  href={`mailto:${supportEmail}`}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Mail size={14} className="me-1" />
                  {supportEmail}
                </Button>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowCancelModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

const AdminPaymentPlanVisibilityToggle = ({ claimId, summary }) => {
  const overrideActive = Boolean(summary?.balance?.overrideActive);
  // Server-computed client view: staff summaries bypass the age gate, so only
  // clientSelfServe can say whether the option opened for the client on its
  // own. When it did, the switch shows ON but locks: turning it off would do
  // nothing (the override only grants early access, it cannot deny).
  const autoOpen = Boolean(summary?.clientSelfServe?.autoOpen);
  const visible = overrideActive || autoOpen;
  const ageDays = summary?.clientSelfServe?.minInvoiceAgeDays;
  const hiddenMeta =
    ageDays != null
      ? `Opens automatically ${ageDays} days ${
          summary?.clientSelfServe?.ageFrom === 'invoiceDueDate'
            ? 'past the due date'
            : 'after invoicing'
        }, or flip to unlock early.`
      : 'Hidden until eligibility opens.';
  const setVisibility = useSetPaymentPlanVisibility(claimId, (result) => {
    toast.success(
      result?.clientPaymentPlanVisible
        ? 'Client self-serve payment plans are visible.'
        : 'Client self-serve payment plans are hidden.',
    );
  });

  const handleToggle = (event) => {
    const enabled = event.target.checked;
    setVisibility.mutate(
      { enabled },
      {
        onError: (err) =>
          toast.error(
            mutationErrorMessage(
              err,
              'Failed to update payment plan visibility.',
            ),
          ),
      },
    );
  };

  return (
    <div className="visibility-row">
      <div className="visibility-copy">
        <div className="visibility-label">Client self-serve</div>
        <div className="visibility-meta">
          {autoOpen
            ? 'Live automatically: eligibility opened, the client can set up a plan now.'
            : overrideActive
              ? 'Unlocked early by staff. Client can set up a plan now.'
              : hiddenMeta}
        </div>
      </div>
      <Form.Check
        type="switch"
        id={`payment-plan-visibility-${claimId}`}
        label={autoOpen ? 'Visible (auto)' : visible ? 'Visible' : 'Hidden'}
        checked={visible}
        disabled={setVisibility.isLoading || autoOpen}
        title={
          autoOpen
            ? 'Eligibility is age based and cannot be switched off.'
            : undefined
        }
        onChange={handleToggle}
        style={{ flex: '0 0 auto', fontWeight: 600, color: '#475569' }}
      />
    </div>
  );
};

// Payment history survives plan changes (Madison, 2026-07-30 — the Joseph
// Guy incident): a cancelled plan disappears from the live-plan card, but
// the money it collected must stay visible. Renders every prior terminal
// plan's PAID installments (cancelled AND completed; the live plan's card
// shows its own). Client-visible too — it's their money, and neutral
// wording ("plan ended") avoids alarming a client mid-reschedule.
const PastPlanPaymentsCard = ({ pastPlans }) => {
  const plans = (pastPlans || []).filter(
    (p) => (p.paidInstallments || []).length > 0,
  );
  if (!plans.length) return null;

  return (
    <Card>
      <div style={{ padding: '1rem 1.25rem' }}>
        <div
          style={{
            fontWeight: 700,
            color: '#0f1c33',
            marginBottom: 2,
          }}
        >
          Payments received
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
          Payments collected under earlier payment plans on this claim. These
          amounts always count toward the balance.
        </div>
        {plans.map((plan) => (
          <div key={plan.paymentPlanId} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
              {plan.status === 'completed' ? 'Plan completed' : 'Plan ended'}
              {plan.cancelledAt
                ? ` ${dayjs(plan.cancelledAt).format('MMM D, YYYY')}`
                : plan.completedAt
                  ? ` ${dayjs(plan.completedAt).format('MMM D, YYYY')}`
                  : ''}{' '}
              · collected {formatCurrency(plan.paidTotal)}
            </div>
            {(plan.paidInstallments || []).map((row) => (
              <div
                key={`${plan.paymentPlanId}-${row.sequence}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 14,
                  color: '#334155',
                  padding: '3px 0',
                }}
              >
                <span>
                  {row.label}
                  {row.paidAt
                    ? ` · paid ${dayjs(row.paidAt).format('MMM D, YYYY')}`
                    : ''}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {formatCurrency(row.amount)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
};

const PaymentPlanSection = ({
  claimId,
  isAdmin,
  summary,
  isLoading,
  refetch,
  isFetching,
}) => {
  if (!claimId) return null;

  // Per-invoice plans: every plan renders as an expandable panel ON its
  // invoice tile (see InvoiceCard) — this section carries only the staff
  // offer card with the claim-scoped self-serve toggle. There is
  // deliberately NO combined "eligible balance" figure anywhere — a number
  // spanning invoices reads like something a single plan could cover
  // (Madison's never-in-question rule).
  const plans = summary?.plans || [];
  const eligibleInvoices = summary?.balance?.eligibleInvoices || [];

  return (
    <>
      {isAdmin ? (
        <Card>
          <PlanSummaryCard>
            <div className="left">
              <span className="eyebrow">
                <Sparkles size={11} />
                Payment Plan
              </span>
              <h4 className="title">Offer a flexible payment plan</h4>
              <p className="meta">
                Each plan covers exactly one invoice. Pick any term and any down
                payment. The client will complete the down payment from their
                billing tab.
              </p>
              <AdminPaymentPlanVisibilityToggle
                claimId={claimId}
                summary={summary}
              />
            </div>
            <div className="right">
              {/* Setup entries live ON the invoice tiles (Nic, 2026-07-30) —
                  this card only carries the claim-scoped self-serve toggle
                  plus a pointer, never a second set of buttons. */}
              <div className="balance-block">
                <div className="balance-label">Eligible invoices</div>
                <div className="balance-meta">
                  {isLoading
                    ? 'Checking eligibility…'
                    : eligibleInvoices.length === 0
                      ? `No open invoices currently qualify${plans.length ? ' for another plan' : ''}.`
                      : `${eligibleInvoices.length} invoice${eligibleInvoices.length === 1 ? '' : 's'} can start a plan. Use the button on the invoice above.`}
                </div>
              </div>
            </div>
          </PlanSummaryCard>
        </Card>
      ) : null}
    </>
  );
};

const CongratulationsEmailStrip = ({ claimId, isAdmin }) => {
  const { data, isLoading } = useFetchBillingCongratulationsEmail(claimId, {
    enabled: !!claimId,
  });

  const sendEmail = useSendBillingCongratulationsEmail(claimId, (result) => {
    if (result?.skipped) {
      toast.info(
        result.reason === 'already_sent_for_rating'
          ? 'Congratulations email was already sent for this rating.'
          : 'Congratulations email was already sent.',
      );
      return;
    }
    toast.success('Congratulations email sent.');
  });

  if (!claimId) return null;
  if (isLoading && !data) return null;
  if (!data) return null;

  const sentAt = data.sentAt;
  const isSending = data.status === 'sending';
  const failed = data.status === 'failed';
  const templateLabel = data.templateLabel || data.candidate?.templateLabel;
  const rating = data.rating ?? data.candidate?.rating;
  const candidateRating = data.candidate?.rating;
  const hasUpdatedRatingCandidate = !!data.hasUpdatedRatingCandidate;
  const hasCandidate = !!data.candidate;
  const canSend = isAdmin && data.canSend && !isSending;

  if (!isAdmin && !sentAt && !hasCandidate) return null;
  if (isAdmin && !sentAt && !hasCandidate) return null;

  const handleSend = () => {
    if (!canSend) return;
    const confirmMessage = hasUpdatedRatingCandidate
      ? `Send an updated congratulations email for the ${candidateRating}% rating? The previous email was for ${rating}%.`
      : 'Send the congratulations email to the client?';
    if (window.confirm(confirmMessage)) {
      sendEmail.mutate();
    }
  };

  const stripSent = !!sentAt && !hasUpdatedRatingCandidate;
  const title = stripSent
    ? `Congratulations email sent${rating != null ? ` for ${rating}%` : ''}`
    : hasUpdatedRatingCandidate
      ? `New rating: ${candidateRating}% — send an updated congratulations email`
      : `Congratulations email ready${candidateRating != null ? ` for ${candidateRating}%` : ''}`;

  const meta = (() => {
    if (failed)
      return 'Last send attempt failed — check the client email and rating event.';
    if (isSending) return 'Sending…';
    if (stripSent) {
      return `${templateLabel || 'Rating-increase template'} · sent ${dayjs(sentAt).format('MMM D, YYYY h:mm A')}`;
    }
    if (hasUpdatedRatingCandidate) {
      return `Previous email was sent for ${rating == null ? 'an earlier' : `${rating}%`} rating.`;
    }
    return templateLabel
      ? `${templateLabel} · ready when you are`
      : 'Ready to send.';
  })();

  return (
    <CongratsStrip $sent={stripSent}>
      <div className="strip-icon">
        {stripSent ? <CheckCircle2 size={18} /> : <PartyPopper size={18} />}
      </div>
      <div className="strip-content">
        <div className="strip-title">{title}</div>
        <div className="strip-meta">{meta}</div>
      </div>
      {isAdmin && (hasCandidate || failed) ? (
        <div className="strip-action">
          <Button
            variant={
              stripSent && !hasUpdatedRatingCandidate
                ? 'outline-success'
                : 'success'
            }
            size="sm"
            disabled={!canSend || sendEmail.isLoading}
            onClick={handleSend}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Mail size={14} className="me-1" />
            {sendEmail.isLoading
              ? 'Sending…'
              : hasUpdatedRatingCandidate
                ? 'Send updated email'
                : stripSent
                  ? 'Resend'
                  : 'Send email'}
          </Button>
        </div>
      ) : null}
    </CongratsStrip>
  );
};

const PaymentPlanStartModal = ({
  show,
  onHide,
  plan,
  claimId,
  summary,
  refetch,
}) => {
  const { user } = useUser();
  const [showEditModal, setShowEditModal] = useState(false);
  const [authorizationAcknowledgments, setAuthorizationAcknowledgments] =
    useState(() => createPaymentPlanAuthorizationState());
  const [typedSignature, setTypedSignature] = useState('');
  const signerName = getPaymentPlanAuthorizationSignerName(user);
  const authorizeSetup = useAuthorizePaymentPlanSetup(claimId, (result) => {
    toast.success(
      'Payment plan authorization accepted. Opening Stripe checkout.',
    );
    openCheckoutUrl(result?.checkoutSession?.url);
    refetch?.();
    onHide();
  });

  useEffect(() => {
    if (!show) return;
    setAuthorizationAcknowledgments(createPaymentPlanAuthorizationState());
    setTypedSignature(signerName);
  }, [show, signerName]);

  if (!plan) return null;

  const depositPercent =
    Number(plan.totalCents) > 0
      ? (Number(plan.downPaymentCents) / Number(plan.totalCents)) * 100
      : 0;
  const isZeroDownPayment = Number(plan.downPaymentCents || 0) === 0;
  const authorizationComplete = isPaymentPlanAuthorizationComplete(
    authorizationAcknowledgments,
    typedSignature,
  );
  const depositProcessing = isPaymentPlanDepositProcessing(plan);
  const handleAuthorizationAcknowledgmentChange = (key, checked) => {
    setAuthorizationAcknowledgments((previous) => ({
      ...previous,
      [key]: checked,
    }));
  };
  const handleStart = () => {
    if (!authorizationComplete) {
      toast.error(
        'Review and accept the payment plan authorization before continuing.',
      );
      return;
    }
    authorizeSetup.mutate(
      {
        paymentPlanId: plan.paymentPlanId,
        paymentPlanAuthorization: buildPaymentPlanAuthorizationPayload({
          acknowledgments: authorizationAcknowledgments,
          typedSignature,
          plan,
          supportContact: summary?.supportContact,
        }),
      },
      {
        onError: (err) =>
          toast.error(
            mutationErrorMessage(
              err,
              'Failed to authorize payment plan setup.',
            ),
          ),
      },
    );
  };

  if (depositProcessing) {
    return (
      <>
        <PaymentPlanModalGlobalStyle />
        <Modal
          show={show}
          onHide={onHide}
          centered
          dialogClassName="billing-plan-modal"
        >
          <ModalHero>
            <button
              type="button"
              className="close-btn"
              onClick={onHide}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="hero-row">
              <div>
                <span className="hero-eyebrow">
                  <Clock3 size={11} />
                  Bank payment processing
                </span>
                <h3 className="hero-title">Your payment is processing</h3>
                <p className="hero-sub">
                  Your bank payment was submitted. ACH payments can take a few
                  business days to confirm.
                </p>
              </div>
              <div className="hero-balance">
                <div className="label">Submitted</div>
                <div className="value">
                  {formatCurrency(plan.downPaymentAmount)}
                </div>
                <div className="meta">no action needed</div>
              </div>
            </div>
          </ModalHero>
          <Modal.Body style={{ padding: '1.25rem 1.6rem' }}>
            <Alert variant="info" className="mb-0">
              We will activate the payment plan after Stripe confirms the bank
              payment. Do not start another checkout for this plan.
            </Alert>
          </Modal.Body>
          <Modal.Footer
            style={{
              borderTop: '1px solid #eef2f7',
              padding: '0.85rem 1.6rem',
              background: '#fafbfd',
            }}
          >
            <Button variant="primary" onClick={onHide}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <>
      <PaymentPlanModalGlobalStyle />
      <Modal
        show={show}
        onHide={onHide}
        centered
        dialogClassName="billing-plan-modal"
      >
        <ModalHero>
          <button
            type="button"
            className="close-btn"
            onClick={onHide}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="hero-row">
            <div>
              <span className="hero-eyebrow">
                <CreditCard size={11} />
                Payment plan ready
              </span>
              <h3 className="hero-title">Start your payment plan</h3>
              {plan.sourceInvoice?.label ? (
                <p
                  className="hero-sub"
                  style={{ fontWeight: 700, marginBottom: '0.25rem' }}
                >
                  For the {plan.sourceInvoice.label}
                </p>
              ) : null}
              <p className="hero-sub">
                {isZeroDownPayment
                  ? 'Save your billing method to activate the schedule. No payment will be charged today.'
                  : 'Make the down payment to activate the schedule. Your billing method will be saved for future installments.'}
              </p>
            </div>
            <div className="hero-balance">
              <div className="label">
                {isZeroDownPayment ? 'Due today' : 'Down payment'}
              </div>
              <div className="value">
                {formatCurrency(plan.downPaymentAmount)}
              </div>
              <div className="meta">
                {isZeroDownPayment
                  ? 'billing method setup only'
                  : `${Math.round(depositPercent)}% down`}
              </div>
            </div>
          </div>
        </ModalHero>

        <Modal.Body style={{ padding: '1.25rem 1.6rem 0.6rem' }}>
          <SummaryGrid>
            <div className="stat">
              <div className="label">Total</div>
              <div className="value">{formatCurrency(plan.totalAmount)}</div>
            </div>
            <div className="stat">
              <div className="label">Down payment</div>
              <div className="value accent">
                {formatCurrency(plan.downPaymentAmount)}
              </div>
            </div>
            <div className="stat">
              <div className="label">Remaining</div>
              <div className="value">
                {formatCurrency(plan.remainingAmount)}
              </div>
            </div>
            <div className="stat">
              <div className="label">Term</div>
              <div className="value">{plan.termMonths} months</div>
            </div>
          </SummaryGrid>

          <ScheduleList>
            <ScheduleHeader>
              <div>#</div>
              <div>Installment</div>
              <div>Due</div>
              <div className="col-amount" style={{ textAlign: 'right' }}>
                Amount
              </div>
            </ScheduleHeader>
            {(plan.installments || []).map((installment) => {
              const isDeposit = installment.sequence === 0;
              const isPaid = installment.status === 'paid';
              return (
                <ScheduleRow
                  key={installment.paymentPlanInstallmentId}
                  $isDeposit={isDeposit}
                  $isPaid={isPaid}
                >
                  <div className="row-num">{installment.sequence + 1}</div>
                  <div>
                    <div className="row-label">
                      {isDeposit
                        ? isZeroDownPayment
                          ? 'Billing method setup'
                          : 'Down payment'
                        : `Installment ${installment.sequence}`}
                    </div>
                    <div className="row-status">
                      {isDeposit
                        ? isZeroDownPayment
                          ? 'no charge today'
                          : 'due now'
                        : installmentStatusLabel(installment.status)}
                    </div>
                  </div>
                  <div className="row-date">
                    {isDeposit
                      ? 'Today'
                      : dayjs(installment.dueDate).format('MMM D, YYYY')}
                  </div>
                  <div className="row-amount">
                    {formatCurrency(installment.amount)}
                  </div>
                </ScheduleRow>
              );
            })}
          </ScheduleList>
          <PaymentPlanAuthorizationTerms
            acknowledgments={authorizationAcknowledgments}
            onAcknowledgmentChange={handleAuthorizationAcknowledgmentChange}
            typedSignature={typedSignature}
            onTypedSignatureChange={setTypedSignature}
            plan={plan}
            signerName={signerName}
            signerEmail={user?.userEmail}
            supportContact={summary?.supportContact}
            disabled={authorizeSetup.isLoading}
          />
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: '1px solid #eef2f7',
            padding: '0.85rem 1.6rem',
            background: '#fafbfd',
          }}
        >
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => setShowEditModal(true)}
            style={{
              borderRadius: 10,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CalendarDays size={16} className="me-1" />
            Edit terms
          </Button>
          <Button
            variant="primary"
            disabled={!authorizationComplete || authorizeSetup.isLoading}
            onClick={handleStart}
            style={{
              borderRadius: 10,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CreditCard size={16} className="me-1" />
            {isZeroDownPayment
              ? 'Save Billing Method & Start Plan'
              : 'Start Payment Plan'}
          </Button>
        </Modal.Footer>
      </Modal>
      <PaymentPlanModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        claimId={claimId}
        isAdmin={false}
        summary={summary}
        refetch={refetch}
        existingPlan={plan}
        onSaved={() => refetch?.()}
      />
    </>
  );
};

const InvoiceCard = ({
  inv,
  claimId,
  isAdmin,
  allPlans = [],
  offeredPaymentPlans = [],
  activePaymentPlans = [],
  processingPaymentPlans = [],
  selfServeEligibleInvoiceIds = [],
  onStartPaymentPlan,
  summary,
  refetchPlans,
}) => {
  const navigate = useNavigate();
  const [planExpanded, setPlanExpanded] = useState(false);
  const hasDiscount =
    Boolean(inv.claimDiscountRequestId) ||
    Number(inv.invoiceDiscountAmount || 0) > 0;
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fields, setFields] = useState({
    invoiceAmount: inv.invoiceAmount ?? '',
    invoiceDueDate: inv.invoiceDueDate
      ? dayjs(inv.invoiceDueDate).format('YYYY-MM-DD')
      : '',
    invoiceNotes: inv.invoiceNotes ?? '',
  });

  const confirmInvoice = useConfirmInvoice(claimId, () =>
    toast.success('Invoice confirmed and sent to client.'),
  );
  const updateInvoice = useUpdateInvoice(claimId, () => {
    toast.success('Invoice updated.');
    setEditing(false);
  });
  const deleteInvoice = useDeleteInvoice(claimId, (result) => {
    const stripeMessage =
      result?.stripeAction === 'voided'
        ? 'voided in Stripe'
        : result?.stripeAction === 'deleted'
          ? 'deleted in Stripe'
          : 'removed locally';
    toast.success(`Invoice deleted and ${stripeMessage}.`);
    setShowDeleteConfirm(false);
  });
  const payoffSession = useCreatePaymentPlanPayoffSession(claimId, (result) => {
    openCheckoutUrl(result?.checkoutSession?.url);
  });

  const handleSave = () => {
    updateInvoice.mutate({
      invoiceId: inv.invoiceId,
      invoiceAmount:
        !hasDiscount && fields.invoiceAmount !== ''
          ? Number(fields.invoiceAmount)
          : undefined,
      invoiceDueDate: fields.invoiceDueDate || undefined,
      invoiceNotes:
        fields.invoiceNotes !== '' ? fields.invoiceNotes : undefined,
    });
  };

  const handleCancel = () => {
    setFields({
      invoiceAmount: inv.invoiceAmount ?? '',
      invoiceDueDate: inv.invoiceDueDate
        ? dayjs(inv.invoiceDueDate).format('YYYY-MM-DD')
        : '',
      invoiceNotes: inv.invoiceNotes ?? '',
    });
    setEditing(false);
  };

  const status = inv.invoiceStatus;
  // Per-invoice plans: a claim can hold multiple concurrent plans, each
  // covering exactly one invoice — resolve THIS invoice's covering plan from
  // the arrays instead of assuming one claim-wide singular.
  const coversThisInvoice = (plan) =>
    (plan?.sourceInvoiceIds || []).includes(inv.invoiceId);
  const coveringActivePlan = activePaymentPlans.find(coversThisInvoice) || null;
  const coveringProcessingPlan =
    processingPaymentPlans.find(coversThisInvoice) || null;
  const coveringOfferedPlan =
    offeredPaymentPlans.find(coversThisInvoice) || null;
  // A plan's source invoice stays open for the life of the plan; "Payment due"
  // is misleading when installments are automated and the next charge may be
  // a month out.
  const isPlanCoveredInvoice = status === 'open' && Boolean(coveringActivePlan);
  const isActivePlanInvoice = !isAdmin && isPlanCoveredInvoice;
  const isProcessingPlanInvoice =
    !isAdmin && status === 'open' && Boolean(coveringProcessingPlan);
  const canStartPaymentPlan =
    !isAdmin &&
    !isActivePlanInvoice &&
    !isProcessingPlanInvoice &&
    status === 'open' &&
    coveringOfferedPlan?.status === 'setup_pending';
  // Self-serve entry lives ON the invoice (Nic, 2026-07-30): an eligible,
  // un-planned open invoice offers its own setup, scoped to itself.
  const canSelfServeSetup =
    status === 'open' &&
    !coveringActivePlan &&
    !coveringProcessingPlan &&
    !coveringOfferedPlan &&
    selfServeEligibleInvoiceIds.includes(inv.invoiceId);
  const canPayPlanInFull =
    isActivePlanInvoice && Number(inv.invoiceAmount || 0) > 0;
  // The plan lives ON its invoice (Nic, 2026-07-30): an expandable panel
  // drops down from the tile instead of separate plan cards below the list.
  // Same per-viewer visibility rules the standalone cards used.
  const coveringPanelPlan =
    allPlans.find((plan) => {
      if (!coversThisInvoice(plan)) return false;
      const depositProcessing = isPaymentPlanDepositProcessing(plan);
      const depositFailed =
        plan?.status === 'setup_pending' &&
        plan?.metadata?.depositStatus === 'failed';
      return (
        isAdmin ||
        depositProcessing ||
        depositFailed ||
        ['active', 'past_due', 'failed'].includes(plan.status)
      );
    }) || null;
  const canDeleteInvoice =
    isAdmin && ['draft', 'open', 'sent'].includes(status);

  // invoiceAmount = Stripe-synced REMAINING balance (0 once paid in full);
  // invoiceBilledAmount = the immutable issued total (migration 140). Paid
  // rows show the total; open/draft rows keep showing the live balance.
  const displayAmount =
    status === 'paid'
      ? (inv.invoiceBilledAmount ?? inv.invoiceAmount)
      : inv.invoiceAmount;

  const handlePayInFull = () => {
    payoffSession.mutate(
      { paymentPlanId: coveringActivePlan.paymentPlanId },
      {
        onError: (err) =>
          toast.error(
            mutationErrorMessage(err, 'Failed to open pay-in-full checkout.'),
          ),
      },
    );
  };
  const handleDeleteInvoice = () => {
    deleteInvoice.mutate(inv.invoiceId, {
      onError: (err) =>
        toast.error(mutationErrorMessage(err, 'Failed to delete invoice.')),
    });
  };

  return (
    <>
      <InvoiceCardStyled>
        <div className="invoice-top">
          <div>
            <InvoiceStatusBadge
              $status={isPlanCoveredInvoice ? 'plan' : status}
            >
              {isPlanCoveredInvoice
                ? 'Payment plan'
                : STATUS_LABELS[status] || status}
            </InvoiceStatusBadge>
          </div>
          <div className="invoice-actions">
            {isAdmin && status === 'draft' && !editing && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            )}
            {canDeleteInvoice && !editing ? (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <Trash2 size={14} className="me-1" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        {editing ? (
          <div className="invoice-grid">
            {hasDiscount ? (
              <Alert
                variant="info"
                className="mb-0"
                style={{ gridColumn: '1 / -1' }}
              >
                This invoice amount is controlled by the approved discount
                request. You can edit the due date and notes.
              </Alert>
            ) : null}
            <DisplayStyled>
              <label>Amount (USD)</label>
              <Form.Control
                type="number"
                step="0.01"
                value={fields.invoiceAmount}
                onChange={(e) =>
                  setFields((f) => ({ ...f, invoiceAmount: e.target.value }))
                }
                disabled={hasDiscount}
              />
            </DisplayStyled>
            <DisplayStyled>
              <label>Due Date</label>
              <Form.Control
                type="date"
                value={fields.invoiceDueDate}
                onChange={(e) =>
                  setFields((f) => ({ ...f, invoiceDueDate: e.target.value }))
                }
              />
            </DisplayStyled>
            <DisplayStyled style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <Form.Control
                as="textarea"
                rows={2}
                value={fields.invoiceNotes}
                onChange={(e) =>
                  setFields((f) => ({ ...f, invoiceNotes: e.target.value }))
                }
              />
            </DisplayStyled>
          </div>
        ) : (
          <div className="invoice-grid">
            {hasDiscount ? (
              <>
                <DisplayStyled>
                  <label>Original amount</label>
                  <span>
                    {formatCurrency(
                      inv.invoiceOriginalAmount || inv.invoiceAmount,
                    )}
                  </span>
                </DisplayStyled>
                <DisplayStyled>
                  <label>Approved discount</label>
                  <span>
                    {formatCurrency(inv.invoiceDiscountAmount)} (
                    {formatPercent(inv.invoiceDiscountPercent)})
                  </span>
                </DisplayStyled>
                <DisplayStyled style={{ gridColumn: '1 / -1' }}>
                  <label>
                    {status === 'paid' ? 'Amount paid' : 'Amount due'}
                  </label>
                  <span
                    style={{
                      fontSize: '1.4rem',
                      fontWeight: 800,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {formatCurrency(displayAmount)}
                  </span>
                </DisplayStyled>
              </>
            ) : (
              <DisplayStyled style={{ gridColumn: '1 / -1' }}>
                <label>
                  {status === 'paid' ? 'Amount paid' : 'Amount due'}
                </label>
                <span
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {formatCurrency(displayAmount)}
                </span>
              </DisplayStyled>
            )}
            <DisplayStyled>
              <label>{status === 'paid' ? 'Paid on' : 'Due date'}</label>
              <span>
                {status === 'paid' && inv.invoicePaidOn
                  ? dayjs(inv.invoicePaidOn).format('MMM D, YYYY')
                  : dayjs(inv.invoiceDueDate).format('MMM D, YYYY')}
              </span>
            </DisplayStyled>
            <DisplayStyled>
              <label>Invoice date</label>
              <span>{dayjs(inv.invoiceDate).format('MMM D, YYYY')}</span>
            </DisplayStyled>
            {inv.invoiceNotes && (
              <DisplayStyled style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <span>{inv.invoiceNotes}</span>
              </DisplayStyled>
            )}
          </div>
        )}

        <div className="invoice-footer">
          {editing ? (
            <>
              <Button
                variant="primary"
                disabled={updateInvoice.isLoading}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {status === 'draft' && isAdmin && (
                <Button
                  variant="warning"
                  disabled={confirmInvoice.isLoading}
                  onClick={() => confirmInvoice.mutate(inv.invoiceId)}
                >
                  Confirm invoice
                </Button>
              )}
              {status === 'open' &&
                inv.stripeInvoiceUrl &&
                !isAdmin &&
                !isActivePlanInvoice &&
                !isProcessingPlanInvoice && (
                  <Button
                    variant="primary"
                    href={inv.stripeInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 10,
                      fontWeight: 700,
                    }}
                  >
                    <CreditCard size={16} className="me-1" />
                    Pay now
                  </Button>
                )}
              {isProcessingPlanInvoice ? (
                <span
                  style={{
                    alignSelf: 'center',
                    color: '#075985',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  }}
                >
                  Bank payment processing
                </span>
              ) : null}
              {canPayPlanInFull ? (
                <Button
                  variant="primary"
                  disabled={payoffSession.isLoading}
                  onClick={handlePayInFull}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Wallet size={16} className="me-1" />
                  Pay in full
                </Button>
              ) : null}
              {/* The plan entry is THE premium action of an eligible tile:
                  one violet button sitting right next to Pay now (Nic,
                  2026-07-30 — no duplicate strip below the invoices, and
                  both buttons share the same rounded shape). */}
              {canStartPaymentPlan ? (
                <Button
                  onClick={() =>
                    onStartPaymentPlan?.(coveringOfferedPlan.paymentPlanId)
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(79, 70, 229, 0.35)',
                  }}
                >
                  <CreditCard size={16} className="me-1" />
                  Start payment plan
                  <ChevronRight size={14} className="ms-1" />
                </Button>
              ) : null}
              {canSelfServeSetup ? (
                <Button
                  onClick={() =>
                    navigate(
                      `/billing/payment-plan/${claimId}/setup?invoiceId=${inv.invoiceId}`,
                    )
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(79, 70, 229, 0.35)',
                  }}
                >
                  <CreditCard size={16} className="me-1" />
                  Set up payment plan
                  <ChevronRight size={14} className="ms-1" />
                </Button>
              ) : null}
              {status !== 'open' &&
                status !== 'draft' &&
                inv.stripeInvoiceUrl && (
                  <Button
                    variant="outline-secondary"
                    href={inv.stripeInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={14} className="me-1" />
                    View invoice
                  </Button>
                )}
            </>
          )}
        </div>

        {coveringPanelPlan ? (
          <>
            <button
              type="button"
              onClick={() => setPlanExpanded((prev) => !prev)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0.7rem 1.3rem',
                border: 'none',
                borderTop: '1px solid #e4dcf8',
                background: planExpanded ? '#f3f0ff' : '#f8f6ff',
                color: '#5b21b6',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <CalendarDays size={15} />
              Payment plan ·{' '}
              {isPaymentPlanDepositProcessing(coveringPanelPlan)
                ? 'Bank payment processing'
                : PLAN_STATUS[coveringPanelPlan.status]?.label ||
                  coveringPanelPlan.status}{' '}
              · {formatCurrency(coveringPanelPlan.remainingAmount)} remaining
              <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                {planExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </span>
            </button>
            {planExpanded ? (
              <div style={{ borderTop: '1px solid #ece6fb' }}>
                <ActivePlanCard
                  claimId={claimId}
                  isAdmin={isAdmin}
                  plan={coveringPanelPlan}
                  refetch={refetchPlans}
                  supportContact={summary?.supportContact}
                  summary={summary}
                  embedded
                />
              </div>
            ) : null}
          </>
        ) : null}
      </InvoiceCardStyled>

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              color: '#0f1c33',
              fontWeight: 650,
              marginBottom: '0.45rem',
            }}
          >
            Delete{' '}
            {formatCurrency(inv.invoiceBilledAmount ?? inv.invoiceAmount)}{' '}
            invoice?
          </p>
          <p style={{ color: '#6b7689', marginBottom: 0, lineHeight: 1.5 }}>
            This removes the invoice from the app. Draft invoices are deleted in
            Stripe; sent invoices are voided in Stripe.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteInvoice.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteInvoice}
            disabled={deleteInvoice.isLoading}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Trash2 size={15} className="me-1" />
            {deleteInvoice.isLoading ? 'Deleting...' : 'Delete invoice'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const BillingTab = ({
  invoices,
  claimId,
  isAdmin,
  disputeHold = null,
  billingFrozen = false,
}) => {
  const {
    data: paymentPlanSummary,
    isLoading: isPaymentPlanLoading,
    refetch: refetchPaymentPlan,
    isFetching: isPaymentPlanFetching,
  } = useFetchPaymentPlanSummary(claimId, {
    enabled: !!claimId,
  });
  // Clients see the payment plan feature when the server says an eligible
  // balance exists (settings-driven: invoices 45+ days past due by default),
  // an admin override is active, or a plan already exists. The old hardcoded
  // test-email allowlist is gone.
  // A frozen claim (dead / dispute hold) hides every client-side plan
  // affordance: the server refuses plan setup on frozen claims, so offering
  // the button would only produce an error. Admins keep the section (the
  // dispute banner above explains the freeze).
  // Per-invoice plans: the summary carries ALL live plans (plans[]), each
  // scoped to one source invoice. Every derivation below is an array.
  const plans = paymentPlanSummary?.plans || [];
  const paymentPlanFeatureEnabled =
    (isAdmin || !billingFrozen) &&
    (isAdmin ||
      Boolean(paymentPlanSummary?.balance?.overrideActive) ||
      plans.length > 0 ||
      Number(paymentPlanSummary?.balance?.balanceCents || 0) > 0);
  const [startPlanModalPlanId, setStartPlanModalPlanId] = useState(null);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const openCount = (invoices || []).filter(
    (inv) => inv.invoiceStatus === 'open',
  ).length;
  const paidCount = (invoices || []).filter(
    (inv) => inv.invoiceStatus === 'paid',
  ).length;
  const offeredPaymentPlans =
    paymentPlanFeatureEnabled && !isAdmin
      ? plans.filter(
          (plan) =>
            plan.status === 'setup_pending' &&
            !isPaymentPlanDepositProcessing(plan),
        )
      : [];
  const activePaymentPlans = paymentPlanFeatureEnabled
    ? plans.filter((plan) =>
        PAYMENT_PLAN_CLIENT_PAYOFF_STATUSES.includes(plan.status),
      )
    : [];
  const processingPaymentPlans = paymentPlanFeatureEnabled
    ? plans.filter((plan) => isPaymentPlanDepositProcessing(plan))
    : [];
  // Per-invoice self-serve entry points (both client and staff): eligible,
  // un-planned open invoices carry their own "Set up payment plan" button.
  const selfServeEligibleInvoiceIds = paymentPlanFeatureEnabled
    ? paymentPlanSummary?.balance?.eligibleInvoiceIds || []
    : [];
  const startPlanModalPlan =
    offeredPaymentPlans.find(
      (plan) => plan.paymentPlanId === startPlanModalPlanId,
    ) || null;

  return (
    <div>
      {isAdmin && disputeHold ? (
        <div
          className="alert alert-danger d-flex align-items-start"
          style={{ gap: 10 }}
          role="alert"
        >
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>This claim is on Dispute Hold</strong>
            {disputeHold.at
              ? ` since ${dayjs(disputeHold.at).format('MMM D, YYYY')}`
              : ''}
            {disputeHold.source === 'stripe_dispute'
              ? ' (formal Stripe dispute)'
              : ' (placed manually)'}
            . No billing emails will be sent and payment plan charges are paused
            until the hold is released from the claims board.
            {disputeHold.note ? (
              <div className="small mt-1 text-muted">
                Note: {disputeHold.note}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {isAdmin ? (
        <CongratulationsEmailStrip claimId={claimId} isAdmin={isAdmin} />
      ) : null}

      <SectionTitle>
        <h4 style={{ display: 'flex', alignItems: 'center' }}>
          <FileText
            size={16}
            style={{ verticalAlign: '-2px', marginRight: 6 }}
          />
          Invoices
        </h4>
        <span className="section-sub">
          {invoices && invoices.length > 0
            ? `${invoices.length} total · ${openCount} open · ${paidCount} paid`
            : 'No invoices yet'}
        </span>
        {isAdmin ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowCreateInvoiceModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontWeight: 700,
            }}
          >
            <Plus size={15} className="me-1" />
            Create invoice
          </Button>
        ) : null}
      </SectionTitle>

      {isAdmin ? (
        <ManualInvoiceModal
          show={showCreateInvoiceModal}
          onHide={() => setShowCreateInvoiceModal(false)}
          claimId={claimId}
        />
      ) : null}

      {invoices && invoices.length > 0 ? (
        invoices.map((inv) => (
          <InvoiceCard
            key={inv.invoiceId}
            inv={inv}
            claimId={claimId}
            isAdmin={isAdmin}
            allPlans={paymentPlanFeatureEnabled ? plans : []}
            offeredPaymentPlans={offeredPaymentPlans}
            activePaymentPlans={activePaymentPlans}
            processingPaymentPlans={processingPaymentPlans}
            selfServeEligibleInvoiceIds={selfServeEligibleInvoiceIds}
            onStartPaymentPlan={(planId) => setStartPlanModalPlanId(planId)}
            summary={paymentPlanSummary}
            refetchPlans={refetchPaymentPlan}
          />
        ))
      ) : (
        <Card>
          <CardBody>
            <div className="text-muted">
              No invoices have been created for this claim yet.
            </div>
          </CardBody>
        </Card>
      )}

      {paymentPlanFeatureEnabled ? (
        <>
          <PaymentPlanStartModal
            show={Boolean(startPlanModalPlan)}
            onHide={() => setStartPlanModalPlanId(null)}
            plan={startPlanModalPlan}
            claimId={claimId}
            summary={paymentPlanSummary}
            refetch={refetchPaymentPlan}
          />

          <PaymentPlanSection
            claimId={claimId}
            isAdmin={isAdmin}
            summary={paymentPlanSummary}
            isLoading={isPaymentPlanLoading}
            refetch={refetchPaymentPlan}
            isFetching={isPaymentPlanFetching}
          />
        </>
      ) : null}

      {/* Payment history is NEVER hostage to plan visibility: after a plan
          ends the client may have no live plan and no eligible balance, but
          their collected money must stay visible on the billing tab. */}
      <PastPlanPaymentsCard pastPlans={paymentPlanSummary?.pastPlans} />
    </div>
  );
};

export default BillingTab;
